// Hong Kong, "Victoria Harbour after dark" (summer). From a crowded Kowloon rooftop we look south across
// Victoria Harbour to the Hong Kong Island skyline: the Bank of China Tower's lit triangular lattice and twin
// masts beside the tall crowned shaft of IFC, Central Plaza's clock bars, a rainbow-banded tower and an LED
// facade, all under the dark ridge of Victoria Peak with the Peak Tower and its tram, and a neon-ringed rooftop
// lightbox sign (the city's billboard) on a waterfront block between Central Plaza and the Bank. A nightly light show
// sweeps coloured beams from the tower tops; a green-and-white double-ended ferry, a red-sailed junk and
// little launches cross the water. On our roof: water tanks, TV antennas, laundry on bamboo poles, dripping
// AC units, neon glyph signs, a cat and pigeons, and the neighbours: an uncle on a stool with his radio and palm fan,
// a couple under one umbrella, an auntie at the washing, someone out for the light show. A warm, humid drizzle and
// harbour haze at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, hash, rng, bayer, hex,
  makeCanvas, rect, ditherFill, makeHalo, drawHalo, reflect, streak, text, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 190;                                      // island waterfront = top row of the harbour
const LEDGE = 232;                                   // top of our rooftop parapet
const FLOOR = 242;                                   // our rooftop floor starts below the parapet
const BOC = { cx: 222 };                             // Bank of China Tower
const IFC = { cx: 268 };                             // the tall crowned tower
const CP = { cx: 98 };                               // Central Plaza (pyramid frame, clock bars, mast), furthest east
const CKC = { x0: 237, x1: 253, top: 92 };           // box tower with an LED facade between the two heroes
const CTR = { cx: 326 };                             // rainbow-banded tower with a stepped spire
const HOPE = { cx: 138 };                            // round tower with a revolving top (Hopewell: just west of Central Plaza)
const HKCEC = { x0: 148, x1: 206 };                  // convention centre: swept wing roof on the water
const PEAKT = { x: 292 };                            // Peak Tower on the ridge
const MOON = { x: 178, y: 26 };
const NB = { r: 60 };                                // neighbour tenement, facade x 0..59
const SIGN = { x: 63, y: 52, w: 14, h: 98 };         // big vertical neon sign hanging off it
const HUT = { x: 410, top: 170 };                    // our rooftop stair hut (right)
const FOCUS = 245;                                   // portrait crop: BoC, the LED tower and IFC
const BB = { x: 160, y: 131, w: 42, h: 16 };         // face of the harbourfront rooftop sign (exported as `billboard`)
// three hidden things (site/js/ui/secrets.js, exported as `secrets`): a cockatoo on top of the neighbour's neon sign
// (the city has wild ones), a teddy bear pegged up to dry at the end of a bamboo pole, and a goldfish that swims
// across the LED tower's facade twice a loop
const COCKATOO = { x: 65, y: 45 };                   // sprite origin: its feet on the sign's top (row 52)
const TEDDY = { x: 109, y: 162 };                    // where it hangs: the column of its middle, the row under the upper pole
const GOLDFISH = [[40, 56], [200, 216]];             // it shows on the facade, outside the light show: 1.5 s fading in and out
const FISH_ROW = 9;                                  // the facade's row of LEDs its top is at, give or take one as it swims

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
const rgbaOf = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const dim = (col, k) => { const c = hex(col); return [Math.round(c[0] * k), Math.round(c[1] * k), Math.round(c[2] * k)]; };
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** draw a glyph given as rows of '#'/'.' into a pix buffer */
function glyphP(p, g, x, y, col) { g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') p.set(x + i, y + j, col); }); }
/** sprite canvas from rows of characters and a colour key ('.' = transparent) */
function sprite(rows, key) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(i, y, col); } });
  return p.done();
}

/* ================= Invented glyphs in the manner of hanzi =================
   Each G7 glyph sets a narrow, irregular block column where a radical would stand beside a boxy component, across an
   empty column joined by one short stroke; each G5 glyph hangs small parts off a full middle bar. None is a real character, no sign spells a word,
   and neither shape can form a centre cross with hooks (a pinwheel). */
const G7 = [
  ['##.####', '#..#..#', '#######', '.#..#..', '##.####', '##.#.#.', '#..####'],
  ['.#..##.', '##.####', '#..#..#', '##.####', '###..#.', '.#.####', '##.#..#'],
  ['##.####', '####.##', '#..####', '##.#..#', '.#.####', '##...#.', '##..###'],
  ['##.#..#', '##.####', '.#.#..#', '###.##.', '#..####', '##..#.#', '##.##.#'],
  ['##.#.#.', '#..####', '##.#..#', '.#.#..#', '##.####', '###.#..', '#..####'],
  ['.#.####', '##.#.#.', '#.#####', '##.#..#', '##.#.##', '.#.#..#', '##.####'],
  ['##.####', '##.#..#', '#..####', '###.#..', '.#.####', '##.#.#.', '##.####'],
  ['##..##.', '#######', '.#.#..#', '##.####', '#....#.', '##.####', '##.#..#'],
];
const G5 = [
  ['##.##', '#...#', '#####', '.#.#.', '##.##'], ['##.#.', '.#.##', '#####', '#..#.', '##.##'],
  ['#..##', '##..#', '#####', '#..##', '##.#.'], ['##.##', '.#.#.', '#####', '##..#', '#..##'],
  ['.#.##', '##..#', '#####', '.#.#.', '##.##'], ['##..#', '#..##', '#####', '##.##', '.#..#'],
  ['#..##', '##..#', '#####', '#..#.', '##.##'], ['##.#.', '#..##', '#####', '.#..#', '##.##'],
];

/* ================= Sky: humid teal-slate night, the city glow warming the haze ================= */
const SKY = ['#060b12', '#08101a', '#0b1520', '#0e1a27', '#12202e', '#172634', '#1d2b3a', '#24313f', '#2c3744',
  '#363c49', '#41434e', '#4d4953', '#5a4f57', '#67555a', '#745b5c', '#82625e', '#906a62'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 190), 1.1) * 0.7;
    v += Math.max(0, 1 - Math.hypot((i - 245) / 330, (y - 190) / 125)) * 0.42;       // the island's light dome in the haze
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 60, (y - MOON.y) / 40)) * 0.05;   // moon haze
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{ const r = rng(12); while (STARS.length < 14) { const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 50); if (x < 70) continue; STARS.push({ x, y, k: 20 + Math.floor(r() * 40), ph: r(), b: 0.15 + r() * 0.3 }); } }
/** a big pale moon softened by the haze (clouds drift across it) */
function renderMoon() {
  const r = 6, s = 15, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - 7, dy = y - 7, dd = Math.hypot(dx, dy);
    if (dd > r + 0.4) continue;
    let col = dd > r - 0.8 ? '#d8c8a8' : '#f2e6c8';
    if ((dx + 2) ** 2 + (dy + 1) ** 2 < 2.2 || (dx - 2) ** 2 + (dy - 2) ** 2 < 1.4) col = '#dccdab';
    if (dx + dy > 5) col = '#c8b898';
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Clouds (tileable humid stratus, lit warm from below by the city) ================= */
const CLOUD_DEFS = [
  { seed: 11, y: 0, h: 46, banks: 3, k: 1, rx: [12, 26], ry: [3, 5.5],
    pal: { top: '#1c2634', body: '#121a25', core: '#101722', bot: '#34363f', bot2: '#262a35' } },
  { seed: 23, y: 46, h: 50, banks: 4, k: 2, rx: [11, 24], ry: [3, 6],
    pal: { top: '#343844', body: '#222834', core: '#1d222d', bot: '#7a605c', bot2: '#524650' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 120, base = h * (0.45 + r() * 0.3);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.3, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.5;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.5 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Victoria Peak: the dark ridge, winding road lights, masts, the Peak Tower ================= */
const RIDGE_PTS = [[-10, 128], [30, 116], [78, 102], [118, 99], [160, 107], [200, 113], [236, 107], [268, 97],
  [298, 89], [324, 81], [342, 79], [360, 84], [398, 98], [440, 107], [490, 116]];
const RIDGE_Y = new Int16Array(W);
for (let x = 0; x < W; x++) {
  let k = 0;
  while (k < RIDGE_PTS.length - 2 && RIDGE_PTS[k + 1][0] <= x) k++;
  const [x0, y0] = RIDGE_PTS[k], [x1, y1] = RIDGE_PTS[k + 1], u = (x - x0) / (x1 - x0);
  const e = 0.5 - 0.5 * Math.cos(Math.PI * clamp(u, 0, 1));
  RIDGE_Y[x] = Math.round(lerp(y0, y1, e) + (vnoise(x / 6, 0, 3) - 0.5) * 3 + (vnoise(x / 2.2, 5, 4) - 0.5) * 1.4);
}
const PEAK_MASTS = [338, 347];
const TRAM = { x0: 307, y0: 128, x1: 294, y1: 96 };  // Peak Tram track on the slope (lower end, upper end)
const HILL = ['#060a0f', '#080e14', '#0b1219', '#0e161e', '#121a23', '#171f28', '#1d252d', '#252b33'];
function renderPeak() {
  const p = pix(W, H), lights = [];
  for (let x = 0; x < W; x++) {
    const top = RIDGE_Y[x];
    for (let y = top; y < WL; y++) {
      const f = (y - top) / (WL - top);
      const v = 0.12 + f * 0.62 + (vnoise(x / 7, y / 5, 8) - 0.5) * 0.28 + (vnoise(x / 2.5, y / 2.5, 9) - 0.5) * 0.12;
      p.set(x, y, pick(HILL, v, x, y));
    }
    p.set(x, top, top < RIDGE_Y[Math.max(0, x - 1)] || x % 3 === 0 ? '#1e2832' : '#18202a');   // faint rim of sky glow
  }
  // winding roads across the slope: rows of sodium and white lamps
  const r = rng(77);
  for (const [dy, amp, ph, a, b] of [[12, 3, 0, 40, 250], [22, 4, 2, 110, 470], [34, 3, 4, 20, 330], [9, 2, 1, 300, 460]]) {
    for (let x = a; x < b; x += 3 + Math.floor(r() * 4)) {
      const y = RIDGE_Y[x] + dy + Math.round(amp * Math.sin(x / 11 + ph));
      if (y >= WL - 30 || r() < 0.2) continue;
      const col = r() < 0.7 ? '#e8a860' : '#c8dcd0';
      p.set(x, y, dim(col, 0.8));
      lights.push({ id: lights.length, x, y, col, n: 3 + Math.floor(r() * 6), ph: r() });
    }
  }
  // radio masts on the summit
  for (const mx of PEAK_MASTS) {
    const top = RIDGE_Y[mx] - 13;
    for (let y = top; y <= RIDGE_Y[mx]; y++) p.set(mx, y, y % 3 === 0 ? '#2a3440' : '#1e2630');
    p.set(mx - 1, top + 4, '#2a3440'); p.set(mx + 1, top + 4, '#2a3440'); p.set(mx - 1, top + 8, '#222a34'); p.set(mx + 1, top + 8, '#222a34');
  }
  // Peak Tram track: a faint dotted line of track lamps
  for (let k = 0; k <= 16; k++) {
    const u = k / 16, x = Math.round(lerp(TRAM.x0, TRAM.x1, u)), y = Math.round(lerp(TRAM.y0, TRAM.y1, u));
    p.set(x, y, k % 2 ? '#2a3038' : '#6a6050');
  }
  // the Peak Tower: its "wok", a crescent bowl with upswept ends (concave on top, curved underneath), held on columns
  // above the lit block of the tram terminus, in the gap below the summit
  const px0 = PEAKT.x, g = RIDGE_Y[px0] + 1, y0 = g - 10;           // y0: the bowl's top at the middle
  for (let dx = -8; dx <= 8; dx++) {
    const a = Math.abs(dx), q = (a / 8) ** 2, top = y0 - Math.round(q * 3), bot = y0 + 3 - Math.round(q * 5);
    for (let y = top; y <= bot; y++) p.set(px0 + dx, y,
      y === top ? (a >= 7 ? '#c8d0dc' : '#eef0f4')                  // the lit rim
        : y === top + 1 && a <= 6 ? (a % 2 ? '#56607a' : '#ffe2a0')  // viewing-deck windows under it
        : y === bot ? (dx < 0 ? '#5a6480' : '#3e4660')               // the curved underside
        : dx < 0 ? '#9aa4bc' : '#747e98');
  }
  for (const dx of [-3, 0, 3]) for (let y = y0 + 4; y < y0 + 6; y++) p.set(px0 + dx, y, dx < 0 ? '#6a7288' : '#4a5268');   // columns
  for (let y = y0 + 6; y <= g; y++) for (let dx = -4; dx <= 4; dx++)
    p.set(px0 + dx, y, Math.abs(dx) === 4 ? '#2a3044' : y === y0 + 6 ? '#8a92a8' : (y & 1) && dx % 2 === 0 ? '#ffd890' : '#3a4258');
  return { c: trim(p), lights };
}

/* ================= Building layers: a static canvas + a canvas of lit windows toggled per floor segment ================= */
const WARM = ['#ffd28a', '#ffc574', '#ffe0a8', '#f0b060'];
const COOL = ['#d6f4ea', '#bfe8de', '#e8fff8', '#c8dcff'];   // Hong Kong flats love cool fluorescent tubes
function makeLayer() { return { p: pix(W, H), lit: pix(W, H), segs: [], blinks: [] }; }
function finishLayer(L) { return { ...trim(L.p), lit: L.lit.done(), segs: L.segs, blinks: L.blinks }; }
/**
 * Floor rows of windows between columns x0..x1 on row y. Some runs are baked lit or dark, the rest
 * become toggling segments (drawn from the lit canvas when their hash says "on").
 */
function floorRow(L, x0, x1, y, r, o = {}) {
  const { warm = 0.55, toggle = 0.45, on = 0.5, pitch = 2, dark = '#0b0f17', min = 3, max = 9 } = o;
  let x = x0;
  while (x <= x1) {
    const len = Math.min(x1 - x + 1, min + Math.floor(r() * (max - min + 1)));
    const pal = r() < warm ? WARM : COOL, col = pal[Math.floor(r() * pal.length)];
    const tg = r() < toggle, lit = !tg && r() < on;
    for (let k = 0; k < len; k += pitch) {
      L.p.set(x + k, y, lit ? dim(col, 0.92) : dark);
      if (tg) L.lit.set(x + k, y, col);
    }
    if (tg) L.segs.push({ id: L.segs.length, x, y, w: len, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.45, off: r() });
    x += len;
  }
}

/* ================= Mid-levels: slim residential towers climbing the Peak's slopes (hazy, far) ================= */
function renderMidLevels() {
  const p = pix(W, H), r = rng(31);
  let x = 20;
  while (x < W) {
    const bw = 3 + Math.floor(r() * 5), gap = r() < 0.35 ? 1 + Math.floor(r() * 4) : 0;
    let ridge = 999;
    for (let i = x; i < Math.min(W, x + bw); i++) ridge = Math.min(ridge, RIDGE_Y[i]);
    const top = ridge + 7 + Math.floor(r() * r() * 30);
    if (top < WL - 30) {
      const tone = r() < 0.5 ? '#131a23' : '#161d27';
      for (let y = top; y < WL; y++) for (let i = x; i < Math.min(W, x + bw); i++) {
        let col = i === x ? '#1b232e' : i === x + bw - 1 ? '#0f141b' : tone;
        if (y === top) col = '#222b36';
        p.set(i, y, col);
      }
      for (let wy = top + 2; wy < WL - 4; wy += 2) for (let wx = x + 1; wx < x + bw - 1; wx += 2)
        if (r() < 0.3) p.set(wx, wy, r() < 0.55 ? (r() < 0.5 ? '#a88458' : '#8a6c4c') : (r() < 0.5 ? '#8aa8a0' : '#6e8a88'));
      if (r() < 0.15) p.set(x + (bw >> 1), top - 1, '#1e2630');
    }
    x += bw + gap;
  }
  return trim(p);
}

/* ================= Back high-rises of Central, Admiralty, Wan Chai and Sheung Wan ================= */
const nearHero = (x0, x1) => (x1 > BOC.cx - 17 && x0 < BOC.cx + 17) || (x1 > IFC.cx - 14 && x0 < IFC.cx + 14) || (x1 > CP.cx - 10 && x0 < CP.cx + 10) || (x1 > CTR.cx - 9 && x0 < CTR.cx + 9);
const BACK_SIGNS = [];                                // glowing glyph panels on some tower tops
function renderBack() {
  const L = makeLayer(), p = L.p, r = rng(47);
  const forced = [
    { x: 172, w: 13, top: 106, crown: 'slant' }, { x: 360, w: 15, top: 104, crown: 'crown' },
    { x: 344, w: 12, top: 112, crown: 'spire' }, { x: 382, w: 16, top: 116, crown: 'sign', g: [3, 6] },
    { x: 106, w: 16, top: 122, crown: 'sign', g: [1, 4] }, { x: 284, w: 14, top: 120, crown: 'twin' },
    { x: 440, w: 14, top: 112, crown: 'spire' },
  ];
  const list = [...forced];
  for (let x = 62; x < W;) {
    const bw = 7 + Math.floor(r() * 11);
    let top = 124 + Math.floor(r() * 40);
    if (nearHero(x, x + bw)) top = Math.max(top, 138);
    if (!forced.some(f => x < f.x + f.w && x + bw > f.x)) list.unshift({ x, w: bw, top, crown: r() < 0.25 ? 'step' : 'flat' });
    x += bw + (r() < 0.2 ? 2 : 0);
  }
  for (const b of list) {
    const topAt = i => {
      switch (b.crown) {
        case 'slant': return b.top + Math.round(i * 0.7);
        case 'step': return b.top + (i < 2 || i >= b.w - 2 ? 4 : 0);
        case 'twin': return b.top + (i >= 4 && i < b.w - 4 ? 5 : 0);
        case 'spire': { const c = (b.w - 1) / 2, d = Math.abs(i - c); return b.top + Math.round(Math.max(0, d - 1) * 1.3); }
        default: return b.top;
      }
    };
    const tone = r() < 0.5 ? '#111822' : '#141b26';
    for (let i = 0; i < b.w; i++) {
      const t0 = topAt(i);
      for (let y = t0; y < WL; y++) {
        let col = i === 0 ? '#1d2531' : i === b.w - 1 ? '#0b1017' : tone;
        if (y === t0) col = '#2a3340';
        p.set(b.x + i, y, col);
      }
    }
    if (b.crown === 'spire') { const c = b.x + ((b.w - 1) >> 1); for (let y = b.top - 10; y < b.top; y++) p.set(c, y, y & 1 ? '#252e3a' : '#323c4a'); L.blinks.push([c, b.top - 11]); }
    else if (b.crown === 'crown' || (b.top < 134 && r() < 0.4)) L.blinks.push([b.x + 1, topAt(1) - 1]);
    if (b.crown === 'crown') for (let i = 1; i < b.w - 1; i++) { p.set(b.x + i, b.top, '#7ae8d8'); if (i % 3 === 1) p.set(b.x + i, b.top - 1, '#5ac8b8'); }
    if (b.crown === 'slant') for (let i = 0; i < b.w; i++) p.set(b.x + i, topAt(i), '#e8c078');
    if (b.crown === 'sign') { p.fill(b.x + 1, b.top + 2, b.w - 2, 9, '#1a0a10'); BACK_SIGNS.push({ x: b.x + 1, y: b.top + 2, w: b.w - 2, g: b.g }); }
    let y0 = b.top + 13;
    if (b.crown !== 'sign') { y0 = 0; for (let i = 1; i < b.w - 1; i++) y0 = Math.max(y0, topAt(i) + 3); }
    for (let y = y0; y < 178; y += 3) floorRow(L, b.x + 1, b.x + b.w - 2, y, r, { warm: 0.5, toggle: 0.28, on: 0.36 });
  }
  return finishLayer(L);
}

/* ================= Bank of China Tower: stepped triangular prisms, white-lit X lattice, twin masts ================= */
function bocSpan(y) {                                 // [left, right] offsets of the silhouette on row y, or null
  if (y >= 180) return [-12, 12];                     // granite podium
  if (y >= 132) return [-10, 10];
  if (y >= 122) return [-10 + Math.round((132 - y) * 0.7), 10];   // first prism ends in a sloped facet (left)
  if (y >= 96) return [-3, 10];
  if (y >= 86) return [-3, 10 - Math.round((96 - y) * 0.7)];      // second one (right)
  if (y >= 66) return [-3, 3];
  if (y >= 58) { const rr = Math.floor((y - 58) / 1.2) - 3; return [-3, Math.min(3, rr)]; }   // sloped glass roof, apex left
  return null;
}
const BOC_MODULES = [                                  // [top, bottom, left, right]: one big X per module
  [157, 179, -10, 10], [132, 157, -10, 10], [109, 121, -3, 10], [96, 109, -3, 10], [76, 85, -3, 3], [66, 76, -3, 3],
];
function renderBoc(L, lines) {
  const p = L.p, cx = BOC.cx, r = rng(88);
  const isLine = new Set(), line = (x, y) => { const k = y * W + x; if (!isLine.has(k)) { isLine.add(k); lines.push({ x, y, s: (186 - y) / 150 }); } };
  for (let y = 58; y < 187; y++) {
    const sp = bocSpan(y);
    for (let dx = sp[0]; dx <= sp[1]; dx++) {
      const x = cx + dx;
      let col;
      if (y >= 180) col = y === 180 ? '#b8a898' : (dx + 12) % 4 === 0 ? '#3a3036' : pick(['#5a4c4e', '#7a6860', '#9a8474'], (y - 180) / 7, x, y);
      else {
        const side = (dx - sp[0]) / Math.max(1, sp[1] - sp[0]);
        col = (y & 1) ? (side < 0.4 ? '#1a2438' : '#141c2c') : (side < 0.4 ? '#1e2a40' : '#172033');
        if (r() < 0.05) col = r() < 0.6 ? '#5a74a0' : '#8a7048';
      }
      p.set(x, y, col);
    }
  }
  // reflective sloped facets: just under each slope
  for (let y = 122; y < 132; y++) { const l = bocSpan(y)[0]; for (let k = 1; k <= 3; k++) p.set(cx + l + k, y, k === 1 ? '#6a7a9e' : '#3e4a66'); }
  for (let y = 86; y < 96; y++) { const rr = bocSpan(y)[1]; for (let k = 1; k <= 3; k++) p.set(cx + rr - k, y, k === 1 ? '#56668a' : '#34405a'); }
  for (let y = 58; y < 66; y++) { const [l, rr] = bocSpan(y); for (let dx = l; dx <= rr; dx++) if (y - (58 + (dx + 3) * 1.2) < 2.2) p.set(cx + dx, y, '#7a8aae'); }
  // white lattice: section outlines, module rails, the big X diagonals, the slope edges
  for (let y = 58; y < 180; y++) { const [l, rr] = bocSpan(y); line(cx + l, y); line(cx + rr, y); }
  for (const [ya, yb, l, rr] of BOC_MODULES) {
    for (let dx = l; dx <= rr; dx++) { line(cx + dx, ya); line(cx + dx, yb); }
    for (let y = ya; y <= yb; y++) {
      const f = (yb - y) / (yb - ya);
      line(cx + Math.round(l + (rr - l) * f), y); line(cx + Math.round(rr - (rr - l) * f), y);
    }
  }
  for (let dx = -3; dx <= 3; dx++) { const y = Math.round(58 + (dx + 3) * 1.2); if (y < 66) line(cx + dx, y); }
  for (const q of lines) p.set(q.x, q.y, q.x > cx + 5 ? '#c4d0ea' : '#e8f0ff');
  // twin masts on the apex
  for (const [dx, top] of [[-3, 40], [-1, 43]]) for (let y = top; y < 60; y++) p.set(cx + dx, y, (y & 1) ? '#8a94aa' : '#c8d0e0');
  L.blinks.push([cx - 3, 39], [cx - 1, 42]);
  p.fill(cx - 4, 50, 4, 1, '#6a7488');
}

/* ================= The tall crowned tower (IFC-style): vertical ribs, three setbacks, a crown of fins ================= */
const ifcHW = y => y >= 76 ? 9 : y >= 64 ? 8 : y >= 54 ? 7 : y >= 46 ? 6 : -1;
const IFC_FINS = [[0, 43], [2, 41], [4, 38], [6, 35]];                // |dx|, fin top row
function renderIfc(L, ribs) {
  const p = L.p, cx = IFC.cx, r = rng(99);
  for (let y = 46; y < 187; y++) {
    const hw = ifcHW(y);
    for (let dx = -hw; dx <= hw; dx++) {
      const x = cx + dx, rib = (dx + 9) % 3 === 0, up = clamp((130 - y) / 84, 0, 1);
      let col;
      if (dx === -hw) col = up > 0.4 ? '#b4c4e0' : '#7a8aa8';
      else if (dx === hw) col = '#131a28';
      else if (rib) col = pick(['#3e4a62', '#5a6a88', '#8a9cbc', '#c4d4f0'], 0.15 + up * 0.85, x, y);
      else col = (y & 1) ? '#172034' : '#1b2438';
      if (ifcHW(y - 1) < hw && Math.abs(dx) < hw) col = '#d8e8ff';           // lit setback ledge
      p.set(x, y, col);
    }
  }
  for (let dx = -6; dx <= 6; dx++) { p.set(cx + dx, 46, '#e0f0ff'); p.set(cx + dx, 47, dx < 3 ? '#b8cce8' : '#8aa0c0'); }
  for (const [a, top] of IFC_FINS) for (const s of a ? [-1, 1] : [1]) for (let y = top; y < 46; y++)
    p.set(cx + s * a, y, y === top ? '#ffffff' : s < 0 || !a ? '#dcecff' : '#9ab4d4');
  for (let dx = -14; dx <= 14; dx++) for (let y = 178; y < 187; y++) {        // podium mall
    if (Math.abs(dx) <= 9 && y < 181) continue;
    p.set(cx + dx, y, y === 178 || (Math.abs(dx) > 9 && y === 181) ? '#9aa8c0' : (y > 182 && (dx + 14) % 3 ? '#ffd8a0' : '#2a3246'));
  }
  // windows in the glass bays between the ribs (toggle by floor segment)
  for (let y = 50; y < 176; y += 2) for (let dx = -8; dx <= 7; dx += 3) {
    const hw = ifcHW(y);
    if (dx <= -hw || dx + 1 >= hw) continue;
    const x = cx + dx, col = r() < 0.7 ? COOL[Math.floor(r() * 4)] : WARM[Math.floor(r() * 4)];
    if (r() < 0.45) { p.set(x, y, dim(col, 0.85)); p.set(x + 1, y, dim(col, 0.7)); continue; }
    L.lit.set(x, y, col); L.lit.set(x + 1, y, col);
    L.segs.push({ id: 5000 + L.segs.length, x, y, w: 2, n: 2 + Math.floor(r() * 6), p: 0.45, off: r() });
  }
  for (let dx = -6; dx <= 6; dx += 3) ribs.push(cx + dx);
  L.blinks.push([cx - 6, 34], [cx + 6, 34]);
}

/* ================= Central Plaza: gold glass shaft; on top an open gold frame (corner legs rising into a pyramid)
   around the lit top floors and a lit glass pyramid, with sky showing between them; the four-bar clock ("Lightime")
   in a slim box at the foot of the long mast; orange neon bars down the shaft ================= */
const CP_BARS = [54, 56, 58, 60];                      // the clock's four bars (rows), in the box on the pyramid's apex
const CP_TOP = 78;                                     // lit roof line of the shaft
const cpHW = y => 2 + ((y - 62) >> 1);                 // pyramid frame half-width, rows 62..71 (1:2 stairs, 2 → 6)
const CP_NEON = [[-4, 95], [-2, 93], [0, 91], [2, 93], [4, 95]];   // vertical neon bars on the shaft: dx, top row
const CP_NEON_END = 113, CP_DOTS = [84, 86, 88];       // they end on one row; a column of three dots above the middle one
function renderCp(L) {
  const p = L.p, cx = CP.cx;
  for (let y = CP_TOP; y < 187; y++) for (let dx = -7; dx <= 7; dx++) {
    const x = cx + dx;
    let col = (y & 1) ? pick(['#6a4822', '#9a6a30', '#c89040'], 0.55 - dx / 20 + (y > 150 ? 0.2 : 0), x, y) : '#3a2816';
    if (dx === -7) col = '#f0c878'; else if (dx === 7) col = '#2e2012'; else if (dx === 0) col = '#e0b060';
    if (y === CP_TOP) col = '#ffe0a0';
    p.set(x, y, col);
  }
  // orange neon: a bar graph of five vertical tubes (the middle one tallest) under a column of three dots
  for (const [dx, top] of CP_NEON) for (let y = top; y <= CP_NEON_END; y++) {
    p.set(cx + dx, y, y === top ? '#ffd8a0' : '#ff9a3c');
    if (dx < 4 && (y & 1)) p.set(cx + dx + 1, y, '#4a2a14');            // dark glass between the tubes
  }
  for (let y = CP_DOTS[0] - 1; y < CP_NEON[2][1]; y++) p.set(cx, y, CP_DOTS.includes(y) ? '#ffb050' : '#2e2012');
  // the top floors between the frame's corner legs: lit teal-white glass bands, gold ledges tie them to the legs
  for (let y = 72; y < CP_TOP; y++) for (let dx = -7; dx <= 7; dx++) {
    const a = Math.abs(dx);
    let col = null;
    if (a === 7) col = dx < 0 ? '#ffd890' : '#b88838';
    else if (a === 6) col = y === 73 || y === 76 ? (dx < 0 ? '#e8b860' : '#9a7030') : null;   // else sky shows through
    else col = y === 72 ? '#2a3436' : (y & 1) ? (dx < 2 ? '#c8f0e4' : '#8ab8ae') : (dx < 2 ? '#4a6a66' : '#34504c');
    if (col) p.set(cx + dx, y, col);
  }
  // pyramid: the gold frame outline, a gap of sky, then the glass pyramid lit warm from inside
  for (let y = 62; y < 72; y++) {
    const hw = cpHW(y), ihw = hw - 2;
    p.set(cx - hw, y, '#ffe2a8'); p.set(cx + hw, y, '#b88838');
    for (let dx = -ihw; dx <= ihw; dx++) {
      const v = 0.95 - Math.abs(dx) / (ihw + 1) * 0.45 - (y - 62) / 10 * 0.25 - (dx > 0 ? 0.1 : 0);
      p.set(cx + dx, y, (dx + y) % 3 === 0 && y > 64 ? '#b89a60' : pick(['#a8884c', '#e8d098', '#fff4d8'], v, cx + dx, y));
    }
  }
  // the clock box on the apex: gold corner edges run on down into the frame; the bars are drawn per frame
  for (let y = 53; y < 62; y++) for (let dx = -2; dx <= 2; dx++)
    p.set(cx + dx, y, y === 53 ? '#f0c878' : dx === -2 ? '#e8b860' : dx === 2 ? '#8a6430' : '#140e0a');
  for (let y = 39; y < 53; y++) p.set(cx, y, y & 1 ? '#9a8a70' : '#d0c0a0');   // the long mast
  L.blinks.push([cx, 38]);
}

/* ================= The rainbow tower: neon bands up the shaft, stepped crown and spire ================= */
const CTR_BANDS = [];
for (let y = 85; y < 152; y += 3) CTR_BANDS.push(y);
function renderCtr(L) {
  const p = L.p, cx = CTR.cx, r = rng(61);
  for (let y = 82; y < 187; y++) for (let dx = -6; dx <= 6; dx++) {
    let col = (y & 1) ? '#141a26' : '#171e2b';
    if (dx === -6) col = '#2a3448'; else if (dx === 6) col = '#0c1018';
    if (CTR_BANDS.includes(y) && Math.abs(dx) < 6) col = '#262838';
    p.set(cx + dx, y, col);
  }
  for (const [y0, y1, hw] of [[76, 82, 4], [70, 76, 2]]) for (let y = y0; y < y1; y++) for (let dx = -hw; dx <= hw; dx++)
    p.set(cx + dx, y, y === y0 ? '#e8e0ff' : dx === -hw ? '#8a90b8' : dx === hw ? '#1a1e2c' : '#2a3044');
  for (let y = 52; y < 70; y++) p.set(cx, y, y < 56 ? '#ffffff' : (y & 1 ? '#b8b0d8' : '#8a84b0'));
  L.blinks.push([cx, 51]);
  for (let y = 156; y < 178; y += 3) floorRow(L, cx - 5, cx + 5, y, r, { warm: 0.4, toggle: 0.5, on: 0.5 });
}

/* ================= The LED-facade box tower between the heroes ================= */
const CKC_COLS = 8, CKC_ROWS = 28;
const ckcCell = (i, j) => [CKC.x0 + 2 + i * 2, CKC.top + 4 + j * 3];
function renderCkc(L) {
  const p = L.p;
  for (let y = CKC.top; y < 187; y++) for (let x = CKC.x0; x <= CKC.x1; x++) {
    let col = (y & 1) ? '#0f141d' : '#121822';
    if (x === CKC.x0) col = '#2a3244'; else if (x === CKC.x1) col = '#0a0e15';
    if (y === CKC.top) col = '#4a5468'; else if (y === CKC.top + 1) col = '#2a3040';
    p.set(x, y, col);
  }
  for (let i = 0; i < CKC_COLS; i++) for (let j = 0; j < CKC_ROWS; j++) { const [x, y] = ckcCell(i, j); p.set(x, y, '#1e2636'); }
  L.blinks.push([CKC.x0 + 1, CKC.top - 1], [CKC.x1 - 1, CKC.top - 1]);
}

/* ================= The round tower with a revolving restaurant ================= */
function renderHope(L) {
  const p = L.p, cx = HOPE.cx, r = rng(71);
  const shade = ['#3a4454', '#2c3444', '#20262f', '#151a22', '#0e1218'];
  for (let y = 110; y < 187; y++) for (let dx = -6; dx <= 6; dx++) p.set(cx + dx, y, pick(shade, 0.12 + (dx + 6) / 12 * 0.85, cx + dx, y));
  for (let y = 104; y < 110; y++) for (let dx = -7; dx <= 7; dx++) {
    const ring = y === 106 || y === 107;
    p.set(cx + dx, y, ring ? (dx % 2 ? '#ffd890' : '#e8a850') : y === 104 ? '#8a94a8' : pick(shade, (dx + 7) / 14, cx + dx, y));
  }
  for (let dx = -5; dx <= 5; dx++) p.set(cx + dx, 103, '#5a6478');
  for (let y = 92; y < 103; y++) p.set(cx, y, y & 1 ? '#3a4454' : '#5a6478');
  L.blinks.push([cx, 91]);
  for (let y = 113; y < 180; y += 3) for (let dx = -5; dx <= 5; dx += 2) {
    const col = r() < 0.6 ? WARM[Math.floor(r() * 4)] : COOL[Math.floor(r() * 4)], k = 1 - Math.abs(dx) / 8;
    if (r() < 0.5) { if (r() < 0.6) p.set(cx + dx, y, dim(col, 0.5 + 0.4 * k)); continue; }
    L.lit.set(cx + dx, y, dim(col, 0.6 + 0.4 * k));
    L.segs.push({ id: 7000 + L.segs.length, x: cx + dx, y, w: 1, n: 2 + Math.floor(r() * 6), p: 0.5, off: r() });
  }
}
function renderHeroes() {
  const L = makeLayer(), lines = [], ribs = [];
  renderHope(L); renderCp(L); renderCtr(L); renderCkc(L); renderIfc(L, ribs); renderBoc(L, lines);
  return { ...finishLayer(L), lines, ribs };
}

/* ================= Waterfront: low blocks, the convention centre's wing roof, porthole tower, piers, shore road ================= */
/** the convention centre's roof blades at column i (0 = east end): [top, bottom] rows, or null. k = 0: the upper
 * blade over the main hall; k = 1: the lower one over the west wing, ending in a long thin point */
function hkcecBlade(i, k) {
  if (k === 0) {                                       // crest over the main hall, a sharp drop to the east tip
    if (i > 42) return null;
    const top = Math.round(173 + 5 * ((i - 15) / (i < 15 ? 15 : 27)) ** 2);
    return [top, top + (i < 2 ? 0 : i < 4 || i > 38 ? 1 : 2)];
  }
  if (i < 18) return null;                             // rising gently west to its point
  const top = Math.round(181.4 - 2.4 * ((i - 18) / 40) ** 1.4);
  return [top, top + (i < 46 ? 2 : i < 53 ? 1 : 0)];
}
const JARD = { cx: 298, top: 138 };
const PIERS = [340, 356, 372];
const SHORE_LAMPS = [];
function renderFront() {
  const L = makeLayer(), p = L.p, r = rng(53);
  for (let x = 62; x < W;) {
    const bw = 6 + Math.floor(r() * 12);
    let top = 160 + Math.floor(r() * 18);
    if (nearHero(x, x + bw) || (x + bw > CKC.x0 && x < CKC.x1 + 2)) top = Math.max(top, 174);
    if (x + bw > HKCEC.x0 && x < HKCEC.x1) { x += bw; continue; }
    const tone = r() < 0.5 ? '#151b25' : '#181f2a';
    for (let y = top; y < WL; y++) for (let i = x; i < Math.min(W, x + bw); i++)
      p.set(i, y, y === top ? '#2c3440' : i === x ? '#202834' : i === x + bw - 1 ? '#0e131a' : tone);
    if (r() < 0.3) { const tx = x + 1 + Math.floor(r() * Math.max(1, bw - 5)); p.fill(tx, top - 3, 4, 3, '#1c232e'); p.fill(tx, top - 3, 4, 1, '#2e3642'); }
    for (let y = top + 3; y < 185; y += 3) floorRow(L, x + 1, x + bw - 2, y, r, { warm: 0.6, toggle: 0.35, on: 0.55 });
    x += bw;
  }
  // porthole tower: pale facade, a grid of round lit windows
  for (let y = JARD.top; y < WL; y++) for (let dx = -6; dx <= 6; dx++) {
    const x = JARD.cx + dx;
    let col = dx === -6 ? '#8a8e9a' : dx === 6 ? '#2a2e38' : (y === JARD.top ? '#b8bcc8' : '#4e525e');
    if (Math.abs(dx) < 6 && y > JARD.top + 1 && (dx + 6) % 2 === 1 && (y - JARD.top) % 3 === 1) col = hash(x, y, 3) < 0.7 ? '#ffe4b0' : '#20242c';
    p.set(x, y, col);
  }
  // convention centre: the swept aluminium wing roof on the water, drawn as metal panels, lit hall glass under it.
  // Two blades: the upper one crests over the tall main hall in the east (left) and ends in a point there; the lower
  // one sweeps further west, lower, over the low wing, and tapers to a point beyond the glass
  for (let i = 0; i <= HKCEC.x1 - HKCEC.x0; i++) {
    const x = HKCEC.x0 + i, A = hkcecBlade(i, 0), B = hkcecBlade(i, 1);
    const glass = i >= 5 && i <= 46;                    // both blades overhang the glass at their tips
    for (let y = A ? A[0] : B ? B[0] : WL; y < WL; y++) {
      let col = null;
      const inA = A && y >= A[0] && y <= A[1], inB = B && y >= B[0] && y <= B[1];
      if (inA || inB) {
        const [t0, t1] = inA ? A : B;
        col = y === t0 ? (inA ? '#d8e2f2' : '#c0cce0')                      // the lit leading edge
          : y === t1 && t1 > t0 + 1 ? '#2a303e'                               // dark underside
          : i % 3 === (inA ? 1 : 2) ? '#5a6478' : (inA ? '#8a96aa' : '#7a8698');   // panels and their seams
      } else if (y === WL - 1) col = '#2a2e38';                               // the promenade edge
      else if (glass && (!A || y > A[1])) {
        const under = B && y < B[0];                                          // high glazing between the blades
        if (i % 3 === 0) col = '#46423e';                                     // tall mullions
        else if (y === 184) col = '#4a4440';                                  // one transom
        else if (y === WL - 2) col = '#ffe4b0';                               // the lit lobby
        else {
          const hall = Math.max(0, 1 - Math.abs(i - 14) / 26);               // the main hall glows brightest
          col = pick(['#5a5048', '#9a8060', '#d4b47c', '#f4dca8'], (under ? 0.2 : 0.3) + hall * 0.5 + (y - 176) / 13 * 0.1, x, y);
        }
      }
      if (col) p.set(x, y, col);
    }
  }
  // piers with lit sheds and a clock-tower cupola
  for (const px0 of PIERS) {
    for (let y = 182; y < WL; y++) for (let x = px0; x < px0 + 12; x++)
      p.set(x, y, y === 182 ? '#3a8a64' : y === 183 ? '#1e4a36' : (x - px0) % 3 === 0 ? '#2a2a30' : '#ffd898');
    p.fill(px0 + 5, 178, 2, 4, '#3a8a64'); p.set(px0 + 5, 177, '#ffe0a0');
  }
  // harbourfront road and railing, lamps
  for (let x = 62; x < W; x++) {
    if (x >= HKCEC.x0 && x <= HKCEC.x1) continue;
    if (PIERS.some(q => x >= q && x < q + 12)) continue;
    p.set(x, WL - 3, '#2a303a'); p.set(x, WL - 2, '#3a404c'); p.set(x, WL - 1, x % 4 === 0 ? '#1a1e26' : '#2a2e38');
  }
  for (let x = 66; x < W; x += 13) if (!(x >= HKCEC.x0 && x <= HKCEC.x1) && !PIERS.some(q => x >= q - 1 && x < q + 13)) {
    p.set(x, WL - 5, '#1c2028'); p.set(x, WL - 4, '#1c2028'); p.set(x, WL - 6, '#ffe2a8'); SHORE_LAMPS.push([x, WL - 6]);
  }
  return finishLayer(L);
}

/* ================= Harbourfront rooftop sign (the city's one billboard) =================
   A backlit lightbox ringed by a jade neon tube, on a base beam and short steel legs on the roof of an old
   harbourfront block right behind the convention centre, between Central Plaza and the Bank of China Tower. The block
   stands in front of the hazy towers behind it and is drawn at its own, nearer scale: a storey is 6 px
   (BB_HOST.floor), so the face (BB, 16 px) is under three storeys tall and the whole sign, tube to roof, under four.
   The face shows the city's own ad (a steaming bamboo basket of dim sum under three invented glyphs), night-graded
   so it stays dimmer than the two towers, or the engine's faces. The block exists only with the sign. */
const BBX = { l: BB.x - 1, r: BB.x + BB.w, t: BB.y - 1, b: BB.y + BB.h };   // the neon tube round the face
// the block the sign stands on: its roof, its first row of windows, a storey's height
const BB_HOST = { x0: BBX.l - 2, x1: BBX.r + 2, top: BBX.b + 4, win: BBX.b + 6, floor: 6 };
const BB_LEGS = [0, 11, 21, 32, 42].map(k => BBX.l + k);                    // each 2 px wide, the last ends on the tube's right
const STEEL = { d: '#0a0e15', m: '#1c2432', h: '#303c4e', l: '#4e5e76' };
function lineP(p, x0, y0, x1, y1, col) {             // 1 px line into a pix buffer, integer endpoints
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(x0 + (x1 - x0) * k / n, y0 + (y1 - y0) * k / n, col);
}
// the night's light on the sign: its own colours stepped down and pulled towards the harbour haze (gradeAd for the
// prerendered ad, bbTone for what is painted over it per frame)
const BB_GRADE = { level: 0.75, tint: '#0e1a27', mix: 0.14 };
const bbTone = col => { const T = hex(BB_GRADE.tint); return toHex(...hex(col).map((v, k) => Math.round(lerp(v * BB_GRADE.level, T[k], BB_GRADE.mix)))); };
const BB_NEON = ['#14302a', '#1b6a50', '#2cb489'];    // the tube: dark, sputtering, lit
const BB_ROOM = [0.74, 0.6, 0.46];                    // a lit window of the block, top row to bottom
/** the block, drawn after the haze (it stands in front of the towers the haze lies on): floors of 2 x 3 px windows
 * that light up and go dark room by room, like the skyline's */
function renderBillboardHost() {
  const L = makeLayer(), p = L.p, r = rng(59), { x0, x1, top, win, floor } = BB_HOST;
  for (let y = top; y < WL; y++) for (let x = x0; x <= x1; x++)
    p.set(x, y, y === top ? '#39424f' : x === x0 ? '#252d39' : x === x1 ? '#0c1118' : y >= win - 1 && (y - win + 1) % floor === 0 ? '#1e2531' : '#171d27');
  const cols = Math.floor((x1 - x0 - 2) / 3);          // windows per floor: 2 px of glass, 1 px of pier
  for (let wy = win; wy + 2 < WL - 2; wy += floor) for (let i = 0; i < cols;) {
    const n = Math.min(cols - i, 1 + Math.floor(r() * 3)), x = x0 + 2 + i * 3, w = n * 3 - 1;   // a room of 1 to 3 windows
    const pal = r() < 0.55 ? WARM : COOL, col = pal[Math.floor(r() * pal.length)];
    const tg = r() < 0.3, lit = !tg && r() < 0.36, seg = { id: L.segs.length, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.45, off: r() };
    for (let j = 0; j < 3; j++) {
      for (let k = 0; k < w; k++) if (k % 3 < 2) {
        p.set(x + k, wy + j, lit ? dim(col, BB_ROOM[j]) : j ? '#0b0f17' : '#0f141d');
        if (tg) L.lit.set(x + k, wy + j, dim(col, BB_ROOM[j]));
      }
      if (tg) L.segs.push({ ...seg, x, y: wy + j, w });
    }
    i += n;
  }
  return finishLayer(L);
}
/** the sign's steel: the base beam under the lightbox and its legs down to the roof (the face and the tube are per frame) */
function renderBillboardFrame() {
  const p = pix(W, H), { l, r: R, b: B } = BBX;
  for (let x = l; x <= R; x++) p.set(x, B + 1, x < l + 4 ? STEEL.l : STEEL.h);
  for (const x of BB_LEGS) for (let y = B + 2; y < BB_HOST.top; y++) { p.set(x, y, '#3e4a5e'); p.set(x + 1, y, STEEL.d); }
  return trim(p);
}
/* the city's own ad: a red lightbox, three invented glyphs (neon, sequenced per frame), "DIM SUM", and an open
   bamboo steamer of three pleated dumplings, steaming (the steam and a glisten on the dumplings are per frame) */
const BB_GLYPHS = [3, 6, 4], BB_GX = 16, BB_GY = 1;
const BASKET = { x: 1, y: 6 };                       // top-left of the basket sprite in the face
const BASKET_ROWS = [
  '..W...W...W..',
  '.WWw.WWw.WWw.',
  'iwwsswwsswwsi',
  'RRRRRRRRRRRRR',
  'aammlmmlmmldd',
  'aammlmmlmmldd',
  'RRRRRRRRRRRRR',
  'aammlmmlmmldd',
  '.RRRRRRRRRRR.'];
const BASKET_COL = { W: '#fff6ea', w: '#f0dcc8', s: '#d0a898', i: '#5a3a1c', R: '#7a5626', a: '#f4e0a8', m: '#d8bc7c',
  l: '#b8985c', d: '#a08048' };
const DUMPLING_TIPS = [2, 6, 10];                    // pleat tips, where the steam rises from
const BOXPAL = ['#560c12', '#6e1016', '#84161a', '#9a1e1e', '#ae2a22'];   // a deep red, so the words carry the light
function renderBillboardAd() {
  const { w, h } = BB;
  const c = ditherFill(w, h, BOXPAL, (i, j) => {         // backlit by two tubes, falling off towards the edges
    const tubes = 0.22 * Math.exp(-(((j - 3.5) / 3) ** 2)) + 0.22 * Math.exp(-(((j - 11.5) / 3) ** 2));
    return Math.max(0, 0.46 + tubes - 0.3 * Math.max(0, 1 - Math.min(i, w - 1 - i) / 4) - 0.25 * Math.max(0, 1 - Math.min(j, h - 1 - j) / 3));
  });
  const x = c.getContext('2d');
  const paint = (rows, ox, oy, key) => rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) rect(x, ox + i, oy + j, 1, 1, col); } });
  paint(BASKET_ROWS, BASKET.x, BASKET.y, BASKET_COL);
  BB_GLYPHS.forEach((gi, k) => G7[gi].forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] === '#') rect(x, BB_GX + k * 9 + i, BB_GY + j, 1, 1, '#6c1416'); }));
  for (const [s, gx] of [['DIM', 16], ['SUM', 30]]) { text(x, s, gx + 1, 10, '#4a080c'); text(x, s, gx, 9, '#ffd84a'); }
  return gradeAd(c, BB_GRADE);
}
function renderBillboardGlyph(gi, lit) {                 // one invented glyph as a neon tube, lit
  const p = pix(7, 7);
  glyphP(p, G7[gi], 0, 0, lit);
  return p.done();
}

/* ================= Water base (dark jade-navy), a stripe of reflected haze near the far shore ================= */
function renderWater() {
  const hh = FLOOR - WL, P = ['#1a2230', '#131b28', '#0e1622', '#0a111b', '#080d16'];
  const p = pix(W, hh);
  for (let y = 0; y < hh; y++) for (let i = 0; i < W; i++) p.set(i, y, pick(P, Math.pow(y / hh, 0.7) * 0.999, i, y));
  return p.done();
}

/* ================= Boats ================= */
/** double-ended green-and-white harbour ferry (symmetric, so it needs no flipped copy) */
function makeFerry() {
  const w = 54, h = 18, p = pix(w, h);
  const G = '#1e7a4a', G2 = '#145836', G3 = '#0c3624', WH = '#eef2ea', WH2 = '#c8d0c8', WH3 = '#9aa49c', LW = '#ffd98a', LW2 = '#f0b058';
  for (let y = 14; y < h; y++) {                                   // hull with rounded ends
    const inset = y === 14 ? 0 : y - 14;
    for (let i = inset; i < w - inset; i++) p.set(i, y, y === 14 ? WH2 : y === 15 ? G : y === 16 ? G2 : G3);
  }
  for (let y = 11; y < 14; y++) for (let i = 2; i < w - 2; i++) {  // open lower deck, lit, with passengers
    let col = (i - 2) % 6 === 0 ? WH : y === 11 ? LW : LW2;
    if (y === 13 && (i - 2) % 6 !== 0) col = '#c88a48';
    if (y === 12 && hash(i, 4, 7) < 0.28 && (i - 2) % 6 !== 0) col = '#3a2a24';
    p.set(i, y, col);
  }
  for (let i = 2; i < w - 2; i++) { p.set(i, 10, G); p.set(i, 9, G2); }                // green belt
  for (let i = 3; i < w - 3; i++) for (let y = 5; y < 9; y++) {                      // upper saloon
    let col = WH;
    if ((y === 6 || y === 7) && (i - 3) % 3 !== 0 && i > 8 && i < w - 9) col = y === 6 ? LW : LW2;
    if (y === 8) col = WH3;
    p.set(i, y, col);
  }
  for (const x0 of [3, w - 9]) for (let y = 3; y < 9; y++) for (let i = x0; i < x0 + 6; i++)   // wheelhouse at each end
    p.set(i, y, y === 3 ? WH2 : (y === 5 || y === 6) && i > x0 && i < x0 + 5 ? '#26323a' : WH);
  for (let i = 8; i < w - 8; i++) p.set(i, 4, i % 2 ? WH3 : WH2);                     // canopy roof
  const m = w >> 1;                                                                   // short funnel amidships:
  for (let i = m - 2; i < m + 2; i++) {                                               // black top with a white star,
    const edge = i === m - 2 ? '#30343e' : '#14161c';                                 // white below
    p.set(i, 0, '#4a4e58');                                                           // rim catching the deck lights
    p.set(i, 1, i === m - 1 || i === m ? '#f0f0e8' : edge);
    p.set(i, 2, edge);
    p.set(i, 3, i === m + 1 ? WH2 : WH);
  }
  for (const x of [5, w - 6]) { for (let y = 0; y < 3; y++) p.set(x, y, '#3a4048'); p.set(x, 0, '#ffffff'); }   // masts at both ends
  for (const x of [10, 22]) for (const [i, s] of [[x, 1], [w - 1 - x, -1]]) {         // life rings: white with red
    p.set(i, 12, '#f4f4ee'); p.set(i + s, 12, '#d8282c'); p.set(i, 13, '#d8282c'); p.set(i + s, 13, '#f4f4ee');
  }
  return p.done();
}
/** red-sailed junk heading left: three batten sails glowing red from the deck lights, high stern */
function makeJunk() {
  const w = 42, h = 34, p = pix(w, h);
  const SAIL = ['#6a1216', '#9a1e1e', '#c83226', '#f05438', '#ff8a5a'];
  const sail = (xm, x1, top, bot, slope) => {         // mast at xm, yard rising towards the stern (slope < 0)
    const rise = Math.round((x1 - xm) * -slope);
    for (let y = top - rise; y < bot; y++) {
      const f = clamp((y - top) / (bot - top), 0, 1), xr = Math.round(x1 + Math.sin(f * Math.PI) * 1.5);
      for (let x = xm; x <= xr; x++) {
        const yy = top + Math.round((x - xm) * slope);
        if (y < yy) continue;
        const batten = (y - yy) % 3 === 2;
        const v = 0.2 + (y - top + rise) / (bot - top + rise) * 0.75 - (x - xm) / (xr - xm + 1) * 0.15;
        p.set(x, y, y === yy ? '#3a0a0c' : batten ? '#4a0c10' : x === xr ? '#5a1014' : pick(SAIL, v, x, y));
      }
    }
    for (let y = top - rise - 2; y < 27; y++) p.set(xm - 1, y, '#2a1a14');
  };
  sail(8, 16, 9, 24, -0.25); sail(18, 30, 2, 25, -0.3); sail(32, 38, 12, 24, -0.2);
  for (let x = 0; x < w; x++) {                                    // hull: low bow left, high stern right
    const top = x > 33 ? 22 + Math.round((w - 1 - x) * 0.3) : x < 4 ? 26 - (4 - x) : 26;
    for (let y = top; y < 33; y++) {
      if (y > 30 && (x < 3 + (y - 30) * 2 || x > w - 3 - (y - 30))) continue;
      p.set(x, y, y === top ? '#b07a44' : y === 28 ? '#ffd07a' : y < 30 ? '#5a3420' : '#2e1a12');
    }
  }
  for (let x = 4; x < 34; x += 3) p.set(x, 27, '#ffe8a8');           // string of deck lights
  return p.done();
}
/** a little launch (walla-walla): dark hull, lit cabin, masthead lamp */
function makeLaunch(flip) {
  const rows = ['....y......', '....k......', '..wwLLw....', '..wLLLw....', 'hhhhhhhhhhh', '.hhhhhhhhh.'];
  const key = { y: '#ffe8b0', k: '#3a3a44', w: '#c8ccd0', L: '#ffcf7a', h: '#1c2028' };
  return sprite(flip ? rows.map(r => [...r].reverse().join('')) : rows, key);
}

/* ================= Neighbour tenement (left): mosaic tiles, caged windows, AC boxes, rooftop sign ================= */
const NWIN = [];
{
  const r = rng(505), kinds = ['cool', 'warm', 'tv', 'cool', 'toggle', 'warm', 'toggle', 'cool', 'warm', 'toggle', 'cool', 'tv', 'warm', 'toggle'];
  [58, 84, 110, 136, 162, 188, 214].forEach((y, fi) => [5, 31].forEach((x, ci) =>
    NWIN.push({ id: NWIN.length, x, y, w: 18, h: 13, kind: kinds[(fi * 2 + ci) % kinds.length], n: 3 + Math.floor(r() * 5), p: 0.55, off: r() })));
}
const NACS = [{ x: 50, y: 72 }, { x: 50, y: 124 }, { x: 9, y: 150 }, { x: 50, y: 176 }, { x: 9, y: 202 }];
const POLES = [{ y: 160, x0: 49, x1: 112, clothes: [[58, 0], [68, 3], [80, 1], [92, 4], [102, 2]] },
  { y: 186, x0: 49, x1: 104, clothes: [[56, 5], [70, 1], [84, 6], [96, 0]] }];
function renderNeighbour() {
  const p = pix(W, H);
  // rooftop: sign frame, water tank, TV antennas
  for (let y = 38; y < 48; y++) for (let x = 6; x < 46; x++) if ((x - 6) % 8 === 0 || y === 38 || (x + y) % 8 === 0) p.set(x, y, '#1e242c');
  for (let y = 30; y < 46; y++) for (let x = 48; x < 58; x++) p.set(x, y, x === 48 ? '#6a7480' : x === 57 ? '#1e242c' : (y - 30) % 5 === 0 ? '#2a323c' : '#3e4852');
  p.fill(48, 29, 10, 1, '#7a8490');
  for (const [ax, top] of [[40, 6], [54, 14]]) {
    for (let y = top; y < 48; y++) p.set(ax, y, '#07080c');
    for (let k = 0; k < 4; k++) { const y = top + 3 + k * 4, hw = 5 - k; for (let dx = -hw; dx <= hw; dx++) p.set(ax + dx, y, '#07080c'); p.set(ax - hw, y - 1, '#3a4250'); }
    p.set(ax, top - 1, '#6a7280');
  }
  // parapet and facade (pale jade mosaic tiles gone grey with grime)
  for (let x = 0; x < NB.r; x++) { p.set(x, 48, '#5a6a6a'); p.set(x, 49, '#3a4648'); p.set(x, 50, '#262e30'); }
  for (let y = 51; y < FLOOR; y++) for (let x = 0; x < NB.r; x++) {
    let col = (y % 3 === 0 || x % 3 === 0) ? '#23302f' : (hash(x >> 1, y >> 1, 5) < 0.08 ? '#344442' : '#2b3937');
    if (x >= NB.r - 2) col = x === NB.r - 1 ? '#161c1c' : '#1e2626';
    if (hash(x, y >> 3, 9) < 0.05 && y % 26 > 16) col = '#1c2524';            // rain streak stains
    p.set(x, y, col);
  }
  for (const y of [55, 81, 107, 133, 159, 185, 211]) for (let x = 0; x < NB.r; x++) { p.set(x, y, '#3a4a48'); p.set(x, y + 1, '#1a2222'); }
  // windows: dark glass + protruding metal cages (lit states drawn per frame)
  for (const w of NWIN) {
    p.fill(w.x - 1, w.y - 1, w.w + 2, w.h + 2, '#4a5456');
    for (let y = w.y; y < w.y + w.h; y++) for (let x = w.x; x < w.x + w.w; x++) p.set(x, y, (x - y) % 7 === 0 ? '#161e24' : '#0e1418');
  }
  for (const a of NACS) {
    p.fill(a.x, a.y, 10, 7, '#8a9096'); p.fill(a.x, a.y, 10, 1, '#b4bac0'); p.fill(a.x, a.y + 6, 10, 1, '#4a5056');
    for (let y = a.y + 2; y < a.y + 6; y += 2) p.fill(a.x + 1, y, 8, 1, '#5a6066');
    p.set(a.x + 1, a.y + 7, '#3a4046'); p.set(a.x + 8, a.y + 7, '#3a4046');
  }
  // drain pipe + sign brackets
  for (let y = 51; y < FLOOR; y++) { p.set(27, y, '#3a4446'); p.set(28, y, y % 18 === 0 ? '#5a6668' : '#2a3234'); }
  for (const y of [SIGN.y + 2, SIGN.y + SIGN.h - 4]) for (let x = NB.r - 1; x < SIGN.x; x++) p.set(x, y, '#3a3e44');
  // bamboo poles out of the windows
  for (const pl of POLES) for (let x = pl.x0; x <= pl.x1; x++) { p.set(x, pl.y, (x % 9 === 0) ? '#6a5a36' : '#a8905a'); p.set(x, pl.y + 1, '#4a3e26'); }
  return trim(p);
}
/** the big vertical neon sign: framed box, glyph tubes (on/off frames) */
const SIGN_G = [0, 4, 7, 2, 5];
function renderSign(on) {
  const { w, h } = SIGN, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
    p.set(x, y, edge ? '#0c0a10' : (x === 1 || x === w - 2 || y === 1 || y === h - 2) ? (on ? '#ff4f9a' : '#3a1a28') : '#140c16');
  }
  SIGN_G.forEach((gi, k) => {
    const gx = 3, gy = 5 + k * 19, g = G7[gi], c = on ? '#34e0a1' : '#1a3a2c';
    if (on) for (let j = -1; j <= 7; j++) for (let i = -1; i <= 7; i++) {
      const near = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]].some(([a, b]) => g[j + b] && g[j + b][i + a] === '#');
      if (near && !(g[j] && g[j][i] === '#')) p.set(gx + i, gy + j, '#10402e');
    }
    glyphP(p, g, gx, gy, on ? (k % 2 ? '#c8ffe8' : '#8affd0') : c);
    if (k < SIGN_G.length - 1) for (let x = 3; x < w - 3; x += 2) p.set(x, gy + 13, on ? '#ff8ac0' : '#2a1420');
  });
  return p.done();
}
function renderRoofSign(on) {                          // red box sign on the neighbour's roof
  const w = 40, h = 12, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
    p.set(x, y, edge ? '#140808' : on ? pick(['#a8141c', '#d8262c', '#ff4a40'], 0.8 - y / h * 0.5 - Math.abs(x - w / 2) / w * 0.4, x, y) : '#3a1012');
  }
  [1, 5, 3, 6].forEach((gi, k) => glyphP(p, G7[gi], 3 + k * 9, 2, on ? '#fff0e0' : '#5a2426'));
  return p.done();
}

/* ================= Our rooftop: stair hut with a tank and antennas, parapet, floor, pots, a red stool ================= */
const DOOR = { x: 420, y: 198, w: 16 };
const TUBE = { x: 419, y: 193, w: 18 };
const HWIN = { x: 450, y: 186, w: 20, h: 12 };
const HAC = { x: 452, y: 206 };
const STOOL = { x: 150 };
const MUG = { x: 169, y: 228 };                      // the uncle's tea, on the parapet cap beside his stool
const RADIO = { x: 133, y: 226 };                    // his transistor radio on the cap, on his other side
const LPOST = { x: 348, y: 197 };
const PUDDLES = [[118, 250, 18, 2], [236, 248, 26, 2], [330, 252, 16, 2]];
const CAT = { x: 292, y: LEDGE - 13 };
const RAIL_Y = 222;
const POSTS = [18, 108, 198, 288, 378];
function renderRoof() {
  const p = pix(W, H), r = rng(606);
  // parapet: lit cap, rough rendered wall, weep hole
  for (let x = 0; x < HUT.x; x++) {
    const glow = 0.5 + 0.5 * Math.max(0, 1 - Math.abs(x - 70) / 90) + 0.3 * Math.max(0, 1 - Math.abs(x - 420) / 60);
    p.set(x, LEDGE, pick(['#4a4a52', '#6a6670', '#8a8088'], glow * 0.8, x, LEDGE));
    p.set(x, LEDGE + 1, '#3a3a44'); p.set(x, LEDGE + 2, '#1a1a22');
    for (let y = LEDGE + 3; y < FLOOR; y++) p.set(x, y, pick(['#16161e', '#1c1c24', '#24242c'], 0.5 + (vnoise(x / 3, y / 2, 4) - 0.5) * 0.9, x, y));
  }
  p.fill(236, LEDGE + 5, 4, 2, '#08080c');
  // railing: posts and a top pipe
  for (let x = 0; x < HUT.x; x++) { p.set(x, RAIL_Y, '#5a626c'); p.set(x, RAIL_Y + 1, '#2a3038'); }
  for (const x of POSTS) for (let y = RAIL_Y; y < LEDGE; y++) { p.set(x, y, '#4a525c'); p.set(x + 1, y, '#23282e'); }
  // floor: wet concrete tiles
  for (let y = FLOOR; y < H; y++) for (let x = 0; x < W; x++) {
    let col = pick(['#0c0c12', '#101018', '#14141c', '#191922'], 0.55 - (y - FLOOR) / 40 + (hash(x >> 2, y >> 1, 6) - 0.5) * 0.4, x, y);
    if ((x + ((y - FLOOR) >> 3) * 5) % 20 === 0 || (y - FLOOR) % 8 === 7) col = '#0a0a10';
    p.set(x, y, col);
  }
  // hut: slab roof, walls, door frame, window, AC box, pipe
  for (let x = HUT.x - 2; x < W; x++) { p.set(x, HUT.top, '#7a7a84'); p.set(x, HUT.top + 1, '#4a4a54'); p.set(x, HUT.top + 2, '#1e1e26'); }
  for (let y = HUT.top + 3; y < FLOOR + 2; y++) for (let x = HUT.x; x < W; x++) {
    let col = pick(['#1a1c24', '#22242c', '#2a2c34', '#34363e'], 0.3 + (x - HUT.x) / 90 * 0.3 + (vnoise(x / 6, y / 9, 7) - 0.5) * 0.3, x, y);
    if (x === HUT.x) col = '#101118';
    if (hash(x, y >> 2, 11) < 0.04) col = '#16181e';
    p.set(x, y, col);
  }
  p.fill(DOOR.x - 2, DOOR.y - 2, DOOR.w + 4, FLOOR - DOOR.y + 2, '#3a3a3e');
  p.fill(DOOR.x, DOOR.y, DOOR.w, FLOOR - DOOR.y, '#0e0c10');
  p.fill(HWIN.x - 1, HWIN.y - 1, HWIN.w + 2, HWIN.h + 2, '#4a4e56'); p.fill(HWIN.x, HWIN.y, HWIN.w, HWIN.h, '#0c0e14');
  for (let x = HWIN.x; x < HWIN.x + HWIN.w; x += 3) for (let y = HWIN.y; y < HWIN.y + HWIN.h; y++) p.set(x, y, '#3a3e46');
  p.fill(TUBE.x, TUBE.y - 1, TUBE.w, 1, '#3a3a42'); p.fill(TUBE.x, TUBE.y, TUBE.w, 2, '#4a5a56');
  p.fill(HAC.x, HAC.y, 14, 9, '#8a9096'); p.fill(HAC.x, HAC.y, 14, 1, '#b8bec4'); p.fill(HAC.x, HAC.y + 8, 14, 1, '#44484e');
  for (let y = HAC.y + 1; y < HAC.y + 8; y++) for (let x = HAC.x + 1; x < HAC.x + 8; x++) { const d = Math.hypot(x - HAC.x - 4, y - HAC.y - 4); if (d < 3.3) p.set(x, y, d > 2.3 ? '#3a3e44' : ((x + y) & 1 ? '#50545a' : '#2a2e34')); }
  for (let y = HAC.y + 2; y < HAC.y + 8; y += 2) p.fill(HAC.x + 9, y, 4, 1, '#5a6066');
  for (let y = HUT.top + 3; y < FLOOR; y++) { p.set(476, y, '#3a3e46'); p.set(477, y, y % 14 === 0 ? '#5a5e66' : '#26282e'); }
  // water tank (stainless, on a stand) and TV antennas on the hut roof
  for (let y = 144; y < HUT.top; y++) for (let x = 420; x < 446; x++) {
    const u = (x - 420) / 25, leg = y > 162;
    if (leg && !(x === 422 || x === 443 || x === 432)) continue;
    const d = Math.abs(u - 0.3);
    let col = leg ? '#2a2c34' : d < 0.06 ? '#b8c2d0' : d < 0.2 ? '#7a8494' : u < 0.7 ? '#4a5260' : '#2a2e38';
    if (!leg && (y === 144 || y === 152 || y === 160)) col = '#5a6270';
    if (!leg && y === 162) col = '#1e2028';
    p.set(x, y, col);
  }
  for (let x = 422; x < 444; x++) p.set(x, 143, x < 430 ? '#c8d0dc' : '#8a94a4');
  for (const [ax, top, n] of [[462, 100, 5], [472, 122, 4]]) {       // TV antennas: dark silhouettes, a glint on top
    for (let y = top; y < HUT.top; y++) { p.set(ax, y, '#07080c'); if (y % 4 === 0) p.set(ax + 1, y, '#07080c'); }
    for (let k = 0; k < n; k++) {
      const y = top + 2 + k * 5, hw = 7 - k;
      for (let dx = -hw; dx <= hw; dx++) { p.set(ax + dx, y, '#07080c'); if (dx < 0 && dx % 2 === 0) p.set(ax + dx, y - 1, '#3a4250'); }
      p.set(ax - hw, y + 1, '#07080c'); p.set(ax + hw, y + 1, '#07080c');
    }
    p.set(ax, top - 1, '#6a7280');
  }
  // laundry-line post fixed to the parapet
  for (let y = LPOST.y; y < LEDGE; y++) { p.set(LPOST.x, y, '#5a626c'); p.set(LPOST.x + 1, y, '#2a3038'); }
  // styrofoam box of spring onions on the parapet
  for (let y = LEDGE - 5; y < LEDGE; y++) for (let x = 186; x < 204; x++) p.set(x, y, y === LEDGE - 5 ? '#e8ecf0' : x === 203 ? '#9aa0a8' : '#c8ced6');
  for (let k = 0; k < 11; k++) { const x = 188 + k * 1.5 + (k % 2), top = LEDGE - 9 - Math.floor(r() * 7); for (let y = top; y < LEDGE - 5; y++) p.set(x, y, y < top + 2 ? '#8ad06a' : '#4a8a3a'); }
  // big glazed pot with a leafy money tree (left, on the floor)
  for (let y = 234; y < 254; y++) {
    const hw = y < 236 ? 12 : Math.round(11 - (y - 236) * 0.2);
    for (let dx = -hw; dx <= hw; dx++) {
      const u = (dx + hw) / (2 * hw + 1);
      let col = pick(['#5ab0a0', '#2a7a70', '#16504a', '#0c2e2c'], 0.15 + u * 0.8, 84 + dx, y);
      if (y === 234 || y === 235) col = u < 0.5 ? '#8ad0c0' : '#3a8a7e';
      if (y === 240 && (dx + 30) % 4 < 2) col = '#e8c878';
      p.set(84 + dx, y, col);
    }
  }
  for (let y = 206; y < 234; y++) p.set(84 + Math.round(Math.sin(y / 5) * 1.2), y, '#3a2a1c');
  for (let k = 0; k < 90; k++) {
    const a = r() * TAU, rr = Math.sqrt(r()), x = 84 + Math.cos(a) * rr * 18, y = 208 + Math.sin(a) * rr * 11;
    const lit = Math.cos(a) < -0.2 && rr > 0.5;
    p.fill(Math.round(x), Math.round(y), 2, 1, lit ? '#5a9a5e' : r() < 0.5 ? '#244a2c' : '#1a3a22');
    if (r() < 0.2) p.set(Math.round(x) + 1, Math.round(y) - 1, '#e8b040');                     // a few little fruit
  }
  // red plastic stool
  for (let x = STOOL.x; x < STOOL.x + 14; x++) { p.set(x, 236, '#ff5a4a'); p.set(x, 237, '#d02a26'); p.set(x, 238, '#8a141a'); }
  for (const [lx, d] of [[STOOL.x + 1, -1], [STOOL.x + 12, 1]]) for (let y = 239; y < 252; y++) { const x = lx + Math.round((y - 239) * 0.12 * d); p.set(x, y, '#c0241e'); p.set(x - d, y, '#7a1216'); }
  for (let x = STOOL.x + 2; x < STOOL.x + 12; x++) p.set(x, 246, '#a01a1a');
  // transistor radio on the parapet cap: maroon case, chrome grille, dial window (lit per frame), handle, aerial
  { const { x: rx, y: ry } = RADIO;
    for (let y = ry; y < ry + 6; y++) for (let x = rx; x < rx + 10; x++) p.set(x, y, y === ry ? '#9a4a40' : x === rx + 9 || y === ry + 5 ? '#3a1612' : '#6a2a24');
    for (let y = ry + 1; y < ry + 5; y++) for (let x = rx + 1; x < rx + 5; x++) p.set(x, y, (x + y) & 1 ? '#a8a8b0' : '#4a4a52');
    p.fill(rx + 6, ry + 2, 3, 2, '#241a10');
    p.fill(rx + 2, ry - 2, 6, 1, '#4a4a52'); p.set(rx + 2, ry - 1, '#4a4a52'); p.set(rx + 7, ry - 1, '#4a4a52');
    lineP(p, rx + 1, ry - 1, rx - 4, ry - 12, '#8a8e9a'); p.set(rx - 4, ry - 13, '#d8dce8'); }
  // puddles on the floor mirror the hazy sky
  for (const [cx, cy, rx, ry] of PUDDLES) for (let y = cy - ry; y <= cy + ry; y++) for (let x = cx - rx; x <= cx + rx; x++) {
    const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
    if (d < 1) p.set(x, y, d > 0.7 ? '#1a1c26' : pick(['#1e2432', '#262c3a', '#303442'], 0.6 - (y - cy + ry) / (2 * ry) * 0.4, x, y));
  }
  return trim(p);
}

/* ================= Laundry (hung by the shoulders, shaded left-lit), the cat, pigeons ================= */
const CLOTHES = [
  { rows: ['###...###', '#########', '#########', '.#######.', '..#####..', '..#####..', '..#####..', '..#####..'], pal: ['#f4f4ee', '#d8dcd8', '#a8b0b4'] },   // tee
  { rows: ['######', '######', '######', '######', '######', '######', '######', '######', '######', '######'], pal: ['#5ae0b0', '#2aa882', '#16705a'], stripe: '#f4f0e0' },   // towel
  { rows: ['#######', '#######', '###.###', '###.###', '##...##', '##...##'], pal: ['#ff6a5a', '#d8323a', '#8a1a24'] },   // shorts
  { rows: ['##.##', '##.##', '##.##', '##.##', '###.###'], pal: ['#e8e0f4', '#c0b8d0', '#8a84a0'] },                   // socks
  { rows: ['.#.#.', '#####', '#####', '#####', '#####', '#####', '#####', '#####'], pal: ['#ffe07a', '#e8b040', '#a87a24'] },   // singlet
  { rows: ['########', '########', '########', '.######.', '.######.', '.######.', '..####..', '..####..', '..####..', '...##...'], pal: ['#8ab4ff', '#5a7ad8', '#34489a'] },   // shirt
  { rows: ['#####', '#####', '#####', '#####', '#####', '#####', '#####'], pal: ['#ff9ac8', '#e0609a', '#a03a6a'] },   // pillowcase
];
function makeCloth(c) {
  const h = c.rows.length, w = Math.max(...c.rows.map(r => r.length)), p = pix(w, h);
  c.rows.forEach((row, y) => { for (let x = 0; x < row.length; x++) if (row[x] === '#') {
    let col = c.pal[x < w * 0.35 ? 0 : x < w * 0.75 ? 1 : 2];
    if (y === h - 1 && col === c.pal[0]) col = c.pal[1];
    if (c.stripe && (y === 2 || y === h - 3)) col = c.stripe;
    p.set(x, y, col);
  } });
  return p.done();
}
const LINE_CLOTHES = [[356, 5], [368, 0], [380, 3], [388, 1], [398, 2]];      // on our own line, x and type
const lineY = x => { const u = (x - LPOST.x) / (HUT.x - LPOST.x); return Math.round(lerp(LPOST.y, 201, u) + Math.sin(Math.PI * clamp(u, 0, 1)) * 4); };
const CAT_ROWS = ['.#.....#...', '.##...##...', '.#######...', '#########..', '.#######...', '..#####....', '..######...', '.########..',
  '.#########.', '##########.', '###########', '###########', '.#########.'];
/** backlit cat seen from behind: black, with a cool rim where the skyline catches its outline */
function makeCat() {
  const h = CAT_ROWS.length, w = CAT_ROWS[0].length, p = pix(w, h);
  const on = (i, j) => j >= 0 && j < h && i >= 0 && i < w && CAT_ROWS[j][i] === '#';
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    if (!on(i, j)) continue;
    let col = '#08080e';
    if (!on(i, j - 1)) col = j < 2 ? '#8a9ab8' : '#5a6a8a';
    else if ((!on(i - 1, j) || !on(i + 1, j)) && j < 9) col = '#2a3448';
    p.set(i, j, col);
  }
  return p.done();
}
const PIGEON = {                                       // 7x5 facing right: G body, D wing, H head, N neck, B beak, L legs
  up: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '..L.L..'],
  peck: ['.......', '.GDDG..', 'GGDDGNH', 'GGGGGHB', '..L.L..'],
  flap: ['D.....D', '.D.HHD.', '.GDDHB.', 'GGDDGG.', '..L.L..'],
};
const PIG_COL = { G: '#6a6e7c', D: '#4a4e5c', H: '#7a7e8e', N: '#3e8a78', B: '#c8b8a0', L: '#b85a50' };
const PIGEONS = [{ x: 415, y: HUT.top - 5, dir: 1, ph: 0.1, k: 37 }, { x: 448, y: HUT.top - 5, dir: -1, ph: 0.55, k: 29 }, { x: 212, y: RAIL_Y - 5, dir: -1, ph: 0.3, k: 43 }];

/* ================= Light show beams (soft, additive), colour ramps ================= */
const SHOW_COLS = ['#34e0a1', '#48d8ff', '#b070ff', '#ff4f9a', '#ffb13b'];
const RAMP = [];
for (let i = 0; i < 60; i++) { const f = i / 60 * SHOW_COLS.length, k = Math.floor(f); RAMP.push(mixHex(SHOW_COLS[k], SHOW_COLS[(k + 1) % SHOW_COLS.length], f - k)); }
const BEAM_L = 200, BEAM_W = 19;
function makeBeam(col) {
  const [c, x] = makeCanvas(BEAM_W, BEAM_L), img = x.createImageData(BEAM_W, BEAM_L), d = img.data, rgb = hex(col), m = (BEAM_W - 1) / 2;
  for (let y = 0; y < BEAM_L; y++) {
    const along = y / (BEAM_L - 1), hw = 1.2 + (1 - along) * 8;
    for (let i = 0; i < BEAM_W; i++) {
      const e = Math.abs(i - m) / hw;
      if (e >= 1) continue;
      const a = Math.pow(1 - e, 1.6) * Math.pow(along, 0.9) * 0.5, o = (y * BEAM_W + i) * 4;
      d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(a * 255);
    }
  }
  x.putImageData(img, 0, 0);
  return c;
}
const BEAMS = [
  { x: IFC.cx, y: 38, base: -0.1, amp: 0.55, k: 22, ph: 0 }, { x: IFC.cx, y: 38, base: 0.4, amp: 0.4, k: 17, ph: 0.4 },
  { x: BOC.cx - 3, y: 42, base: -0.3, amp: 0.5, k: 19, ph: 0.2 }, { x: CP.cx, y: 46, base: -0.45, amp: 0.4, k: 23, ph: 0.7 },
  { x: CTR.cx, y: 54, base: 0.35, amp: 0.45, k: 21, ph: 0.55 }, { x: 367, y: 104, base: 0.55, amp: 0.3, k: 25, ph: 0.1 },
  { x: 178, y: 106, base: -0.6, amp: 0.3, k: 18, ph: 0.85 },
];
const SHOW = [104, 172];                              // the nightly light show (loop seconds)
const showLevel = t => env(t, SHOW[0], SHOW[1], 3);
// the real clock's colours: red, white, purple, yellow, pink and green
const CP_SETS = [['#ff4040', '#ff4040', '#ffffff', '#ffffff'], ['#ffffff', '#ffd040', '#ffd040', '#ffd040'], ['#40ff9a', '#40ff9a', '#40ff9a', '#ff4040'],
  ['#c070ff', '#ffffff', '#c070ff', '#ffffff'], ['#ff6ac0', '#ff6ac0', '#ffd040', '#ffd040'], ['#ff4040', '#ffd040', '#40ff9a', '#ff6ac0']];

/* ================= Weather: drizzle at three depths, rings on the water, harbour mist ================= */
const RAIN_LIGHTS = [
  { x: IFC.cx, y: 80, r: 30, c: [220, 236, 255] }, { x: BOC.cx, y: 110, r: 26, c: [220, 232, 255] },
  { x: CP.cx, y: 100, r: 24, c: [255, 210, 140] }, { x: SIGN.x + 7, y: 100, r: 34, c: [120, 255, 200] },
  { x: 428, y: 196, r: 34, c: [220, 255, 240] }, { x: 26, y: 30, r: 26, c: [255, 120, 110] }, { x: 428, y: 226, r: 22, c: [255, 210, 150] },
  { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), r: 20, c: [118, 204, 170], bb: true },   // the rooftop sign
].map(l => ({ ...l, r2: l.r * l.r }));
// a picked weather (site/js/engine/weather.js): Hong Kong draws its own drizzle, so Rain gets the heavier shared rain; it
// lands on the Peak's slopes behind Central, on the harbour and on our rooftop floor, and catches the same lights as the
// drizzle. The AC units' drips are their own condensation, not the drizzle's, so they stay in every pick
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[128, 188], [WL + 3, LEDGE - 3], [FLOOR + 2, H - 1]];
const wxLight = l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: toHex(...l.c) });
const WX_LEAVES = { wind: [0, 12, W, 200] };          // no tree grows on a Kowloon rooftop: the wind carries the leaves over the harbour
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.filter(l => !l.bb).map(wxLight), leaves: WX_LEAVES };
const WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS.map(wxLight), leaves: WX_LEAVES };   // + the rooftop sign
function litAt(x, y, board) {
  for (let i = 0; i < RAIN_LIGHTS.length; i++) {
    const l = RAIN_LIGHTS[i], dx = x - l.x, dy = y - l.y;
    if ((board || !l.bb) && dx * dx + dy * dy < l.r2) return i;
  }
  return -1;
}
function nearLand(x, a, b) {
  if (x >= HUT.x) return a < 0.3 ? HUT.top : FLOOR + 2 + Math.floor(b * 24);
  if (x >= NB.r && a < 0.35) return LEDGE;
  return FLOOR + 2 + Math.floor(b * 24);
}
const RAIN = [
  { count: 150, nMin: 90, nMax: 110, fall: 180, ff: 1, len: 3, slope: -0.1, a: 0.2, land: (x, a) => 120 + Math.floor(a * 70) },
  { count: 80, nMin: 120, nMax: 140, fall: 230, ff: 0.9, len: 4, slope: -0.14, a: 0.24, water: true, land: (x, a) => WL + 3 + Math.floor(a * (LEDGE - WL - 6)) },
  { count: 28, nMin: 150, nMax: 170, fall: 300, ff: 0.86, len: 7, slope: -0.2, a: 0.34, land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(100 + li);
  L.col = `rgba(196,190,210,${L.a})`;
  L.splash = li === 2 ? '#c8c4d8' : '#9aa0b8';
  L.lit = RAIN_LIGHTS.map(l => rgbaOf(l.c, Math.min(0.85, L.a + 0.25)));
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
const RINGS = [];
{ const r = rng(71); for (let i = 0; i < 30; i++) RINGS.push({ id: i, n: 90 + Math.floor(r() * 40), ph: r() }); }
const FOG_DEFS = [
  { seed: 5, y: 128, h: 68, k: 1, dir: 1, top: '#46464f', bot: '#6a5a5e', alpha: 0.6, dens: t => 0.55 + 0.2 * wave(t, 3, 0.1) + 0.08 * wave(t, 7, 0.6) },
  { seed: 8, y: 186, h: 50, k: 2, dir: -1, top: '#5e5864', bot: '#6a6068', alpha: 0.48, dens: t => 0.45 + 0.28 * wave(t, 4, 0.35) + 0.08 * wave(t, 9, 0.2) },
  { seed: 13, y: 64, h: 44, k: 1, dir: -1, top: '#2c323e', bot: '#403e4a', alpha: 0.5, dens: t => 0.4 + 0.25 * wave(t, 2, 0.6) + 0.1 * wave(t, 5, 0.1) },
];
const FOG_LEVELS = 8;
function makeFog(def) {
  const h = def.h, n = W * h, F = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41) + 0.06 * Math.sin(TAU * 23 * x / W - y * 0.7);
    F[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.25, [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
    for (let q = 0; q < n; q++) {
      const x = q % W, y = (q / W) | 0, v = Math.floor(Math.min(1, F[q] * D) * 4 + bayer(x, y)) / 4;
      if (v <= 0) continue;
      const m = y / (h - 1), o = q * 4;
      d[o] = A[0] + (B[0] - A[0]) * m; d[o + 1] = A[1] + (B[1] - A[1]) * m; d[o + 2] = A[2] + (B[2] - A[2]) * m;
      d[o + 3] = Math.round(Math.min(1, v) * 255);
    }
    cx.putImageData(img, 0, 0);
    levels.push(c);
  }
  return { ...def, levels };
}

/* ================= Timed events (loop seconds) ================= */
// every pass starts and ends hidden (behind the tenement or the hut), wakes included
const FERRIES = [
  { a: 14, b: 116, x0: 2, x1: 500, wl: 214 },         // emerges from behind the tenement, left to right
  { a: 134, b: 236, x0: 412, x1: -90, wl: 223 },      // back again in the nearer lane
];
const HORNS = [15, 134.6];
const JUNK = { a: 30, b: 210, x0: 412, x1: -40, wl: 205 };
const LAUNCHES = [{ k: 2, ph: 0.1, wl: 209, dir: 1 }, { k: 3, ph: 0.55, wl: 229, dir: -1 }, { k: 1, ph: 0.8, wl: 199, dir: -1 }];
const CARS = [];
{ const r = rng(73); for (let i = 0; i < 12; i++) CARS.push({ dir: r() < 0.5 ? 1 : -1, n: 3 + Math.floor(r() * 5), ph: r() }); }
const SIGN_FLICK = [[41, 42.4], [118.2, 119], [188, 190.6]];
const ROOF_FLICK = [[76, 77.4], [201.5, 202.3]];
const TUBE_FLICK = [[58, 59.8], [163, 164.2], [226, 227.5]];
const EAR_FLICKS = [21.4, 64.8, 109.2, 150.5, 197.7, 231.1];
const HEAD_TURNS = [[36, 44], [128, 137], [206, 212]];
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };   // 160 per loop
const TVC = ['#3c6cff', '#6d9cff', '#a8ccff', '#2a44b8', '#e2efff', '#5a7ad8'];
const STREAKS = [
  { x: IFC.cx, w: 10, col: '#cfe0ff', a: 0.4, seed: 1 }, { x: IFC.cx, w: 3, col: '#ffffff', a: 0.5, seed: 8 },
  { x: BOC.cx, w: 9, col: '#dfe8ff', a: 0.38, seed: 2 }, { x: BOC.cx - 2, w: 3, col: '#ffffff', a: 0.45, seed: 9 },
  { x: CP.cx, w: 8, col: '#ffc868', a: 0.42, seed: 3 }, { x: HKCEC.x0 + 24, w: 24, col: '#f0dcb0', a: 0.26, seed: 4 },
  { x: JARD.cx, w: 5, col: '#ffe0b0', a: 0.26, seed: 5 }, { x: HOPE.cx, w: 4, col: '#ffc878', a: 0.24, seed: 6 },
  ...PIERS.map((x, i) => ({ x: x + 6, w: 6, col: '#ffd898', a: 0.28, seed: 7 + i })),
];

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, PEAK, MID_C, BACK, HERO, FRONT, WATER_C, FERRY_C, JUNK_C, LAUNCH_C, NB_C, SIGN_C, RSIGN_C,
  ROOF_C, CLOTH_C, UMB_C, CAT_C, PIG_C, BEAM_C, FOGS, VIG_C, BSIGN_C, PUFF_C, BBL, BBF_C, BB_AD, BB_GLY_C, PITCH, COCK_C, TEDDY_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#c8b89a', 0.16, 1.6), moonCore: makeHalo(14, '#f4e8d0', 0.24, 1.4),
    ifc: makeHalo(70, '#9ab8ff', 0.12, 1.6), ifcTop: makeHalo(20, '#e0f0ff', 0.32), boc: makeHalo(60, '#a8c0ff', 0.12, 1.6),
    cp: makeHalo(40, '#ffb850', 0.16, 1.6), cpTop: makeHalo(12, '#ffe0a0', 0.34), cpClock: makeHalo(6, '#f0e8ff', 0.3),
    ctr: makeHalo(30, '#c080ff', 0.14),
    ckc: makeHalo(30, '#bfe8ff', 0.14), hkcec: makeHalo(38, '#f0dcb4', 0.15), peakT: makeHalo(12, '#ffe2a8', 0.34),
    red: makeHalo(5, '#ff3030', 0.6, 1), strobe: makeHalo(4, '#e8f0ff', 0.6, 1), shore: makeHalo(5, '#ffb45a', 0.3), pier: makeHalo(10, '#ffd090', 0.3),
    beamSrc: SHOW_COLS.map(c => makeHalo(10, c, 0.5, 1.3)), tram: makeHalo(6, '#ffe0a0', 0.5),
    ferry: makeHalo(28, '#ffd8a0', 0.16), junk: makeHalo(26, '#ff5a3a', 0.2), launch: makeHalo(7, '#ffd070', 0.4),
    sign: makeHalo(40, '#34e0a1', 0.22), signPink: makeHalo(22, '#ff4f9a', 0.2), roofSign: makeHalo(30, '#ff3a3a', 0.2),
    bsign: makeHalo(12, '#ff4a4a', 0.28), win: makeHalo(12, '#ffb45a', 0.12), winCool: makeHalo(12, '#bff0e0', 0.12), tv: makeHalo(12, '#5a8cff', 0.26),
    tube: makeHalo(40, '#d8fff0', 0.2, 1.5), tubeCore: makeHalo(12, '#f0fff8', 0.4), door: makeHalo(30, '#ffc070', 0.2), hwin: makeHalo(16, '#ffc878', 0.18),
    street: makeHalo(60, '#ff5aa8', 0.08, 1.4), streetJade: makeHalo(50, '#34e0a1', 0.07, 1.4), mug: makeHalo(8, '#ffd8a0', 0.2),
    bbGlow: makeHalo(26, '#2cb489', 0.07, 1.5), dial: makeHalo(4, '#ffb040', 0.4),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  PEAK = renderPeak(); MID_C = renderMidLevels(); BACK = renderBack(); HERO = renderHeroes(); FRONT = renderFront();
  // the rooftop sign and its block hide whatever skyline sits behind them, aircraft lamps included
  BACK.blinksBB = BACK.blinks.filter(([x, y]) => !(x >= BB_HOST.x0 - 2 && x <= BB_HOST.x1 + 2 && y >= BBX.t - 2 && y < WL));
  BBL = renderBillboardHost(); BBF_C = renderBillboardFrame(); BB_AD = renderBillboardAd();
  BB_GLY_C = BB_GLYPHS.map(gi => renderBillboardGlyph(gi, bbTone('#f66e40')));   // red-orange tubes: they glow, the towers stay the brightest
  PITCH = makePitch(BB.w, BB.h, { accent: '#34e0a1' });
  WATER_C = renderWater(); FERRY_C = makeFerry(); JUNK_C = makeJunk(); LAUNCH_C = [makeLaunch(false), makeLaunch(true)];
  NB_C = renderNeighbour(); SIGN_C = [renderSign(false), renderSign(true)]; RSIGN_C = [renderRoofSign(false), renderRoofSign(true)];
  ROOF_C = renderRoof(); CLOTH_C = CLOTHES.map(makeCloth); UMB_C = makeUmbrella(); bakePeople();
  CAT_C = makeCat();
  COCK_C = COCKATOO_ROWS.map(rows => sprite(rows, COCKATOO_KEY)); TEDDY_C = sprite(TEDDY_ROWS, TEDDY_KEY);
  PIG_C = {};
  for (const k in PIGEON) PIG_C[k] = [false, true].map(fl => sprite(PIGEON[k].map(r => fl ? [...r].reverse().join('') : r), PIG_COL));
  BEAM_C = SHOW_COLS.map(makeBeam);
  FOGS = FOG_DEFS.map(makeFog);
  BSIGN_C = BACK_SIGNS.map(s => {
    const p = pix(s.w, 9);
    for (let y = 0; y < 9; y++) for (let x = 0; x < s.w; x++) p.set(x, y, x === 0 || y === 0 || x === s.w - 1 || y === 8 ? '#3a0c12' : '#c81e28');
    s.g.forEach((gi, k) => glyphP(p, G5[gi], 1 + k * 6, 2, '#fff0e8'));
    return p.done();
  });
  PUFF_C = [];
  for (let pr = 1; pr <= 3; pr++) {
    const s = pr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - pr, y - pr) / pr; if (d < 1 && (1 - d) * 1.7 > bayer(i, y) * 0.9) p.set(i, y, '#e8e4ee'); }
    PUFF_C[pr] = p.done();
  }
  VIG_C = renderVignette();
}
function renderVignette() {
  const p = pix(W, H), c = [3, 5, 10];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.45 * 255));
  }
  return p.done();
}

/* ================= Per-frame: sky, the Peak, the skyline ================= */
function drawScroll(cv, y, k, t, dir = 1) {
  let off = Math.floor(frac(k * t / LOOP) * W);
  if (dir < 0) off = (W - off) % W;
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawFog(f, t) {
  const D = clamp(f.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k;
  for (const [cv, a] of [[f.levels[k], 1 - fr], [f.levels[k + 1], fr]]) {
    if (a * f.alpha < 0.004) continue;
    ctx.globalAlpha = a * f.alpha;
    drawScroll(cv, f.y, f.k, t, f.dir);
  }
  ctx.globalAlpha = 1;
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#d8e0ea';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9); halo(HL.moonCore, MOON.x, MOON.y, 0.8);
  ctx.globalAlpha = 0.85; ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7); ctx.globalAlpha = 1;
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
}
const HELI = { t0: 174, t1: 232, x0: W + 10, x1: -14, y0: 64, y1: 52 };
function drawHeli(t) {                                 // a helicopter crossing high over the harbour, behind the towers
  if (t < HELI.t0 || t >= HELI.t1) return;
  const s = (t - HELI.t0) / (HELI.t1 - HELI.t0), x = Math.round(lerp(HELI.x0, HELI.x1, s)), y = Math.round(lerp(HELI.y0, HELI.y1, s) + wave(t, 40) * 0.8);
  rect(ctx, x - 2, y, 5, 2, '#141820'); rect(ctx, x + 3, y, 5, 1, '#141820'); rect(ctx, x + 7, y - 1, 1, 2, '#141820');
  rect(ctx, x - 2, y, 1, 1, '#3a4a5e'); rect(ctx, x - 1, y + 2, 4, 1, '#0e1016');
  const rot = step(t, 2400) & 1;
  ctx.globalAlpha = 0.5; rect(ctx, x - 4 + rot, y - 1, 9 - rot * 2, 1, '#a8b0c0'); ctx.globalAlpha = 1;
  rect(ctx, x + 7, y - 1, 1, 1, frac(t / 1.2) < 0.5 ? '#ff3a3a' : '#5a1818');
  if (frac(t) < 0.1) { rect(ctx, x, y + 1, 1, 1, '#ffffff'); halo(HL.strobe, x, y + 1, 0.9); }
}
function tramAt(t) {                                   // 4 round trips per loop, pausing at both stations
  const tau = t % 60, u = tau < 22 ? smooth(0, 22, tau) : tau < 30 ? 1 : tau < 52 ? 1 - smooth(30, 52, tau) : 0;
  return [Math.round(lerp(TRAM.x0, TRAM.x1, u)), Math.round(lerp(TRAM.y0, TRAM.y1, u))];
}
function drawPeak(t) {
  blit(PEAK.c);
  const s = step(t, 480);
  for (const l of PEAK.lights) {
    let a = 0.55 + 0.45 * wave(t, l.n * 7, l.ph);
    if (hash(l.id, s, 9) < 0.05) a *= 0.3;
    ctx.globalAlpha = a; ctx.fillStyle = l.col; ctx.fillRect(l.x, l.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  halo(HL.peakT, PEAKT.x, RIDGE_Y[PEAKT.x] - 8, 0.9);
  const [tx, ty] = tramAt(t);
  rect(ctx, tx - 1, ty - 1, 3, 2, '#ffe6a8'); rect(ctx, tx - 1, ty, 1, 1, '#c88a40');
  halo(HL.tram, tx, ty, 0.8);
  drawFog(FOGS[2], t);                                // cloud cap dragging over the ridge
}
function drawLayer(L, t, salt) {
  blit(L);
  let rx = 0, ry = 0, rw = 0;                          // runs of adjacent lit segments are copied in one go
  for (const s of L.segs) {
    if (hash(s.id + salt, Math.floor(frac(t / LOOP + s.off) * s.n), 4) >= s.p) continue;
    if (rw && s.y === ry && s.x === rx + rw) { rw += s.w; continue; }
    if (rw) ctx.drawImage(L.lit, rx, ry, rw, 1, rx, ry, rw, 1);
    rx = s.x; ry = s.y; rw = s.w;
  }
  if (rw) ctx.drawImage(L.lit, rx, ry, rw, 1, rx, ry, rw, 1);
}
function drawBackSigns(t) {
  BACK_SIGNS.forEach((s, i) => {
    const off = frac(t / 30 + i * 0.37) > 0.95 && hash(i, Math.floor(t * 12), 5) < 0.5;
    if (off) return;
    ctx.drawImage(BSIGN_C[i], s.x, s.y);
    halo(HL.bsign, s.x + s.w / 2, s.y + 4, 0.8);
  });
}
/* hidden things: the goldfish on the LED facade (drawHeroFx), the cockatoo on the neon sign, the teddy on the pole (drawNeighbour) */
const FISH_ROWS = [['..ooo..t', '.owooo.t', 'oooooott', '.ooooo.t', '..ooo..t'], ['..ooo...', '.owooo.t', 'oooooott', '.ooooo.t', '..ooo...']];   // facing left: o body, w eye, t tail
const COCKATOO_ROWS = [['.y....', 'yyww..', '.wwkg.', '.wwww.', '.wwws.', '..wss.', '..f.f.'], ['......', '.yww..', '.wwkg.', '.wwww.', '.wwws.', '..wss.', '..f.f.']];   // crest up, crest down
const COCKATOO_KEY = { w: '#e4e6ee', s: '#b4a6c0', y: '#e8cc4a', k: '#14141a', g: '#3c3c46', f: '#5c5c66' };
const TEDDY_ROWS = ['e...e', 'bbbbb', 'bkbkb', 'bbmbb', '.bbb.', 'bbmbd', '.bbd.', 'b...d'];
const TEDDY_KEY = { e: '#b88248', b: '#a8743e', d: '#7a5028', m: '#d8b480', k: '#1a1210' };
function drawCockatoo(t) {
  ctx.drawImage(COCK_C[hash(8, step(t, 16), 7) < 0.35 ? 0 : 1], COCKATOO.x, COCKATOO.y);   // its crest goes up now and then
}
function drawTeddy(t) {
  const h = TEDDY_C.height, sw = sway(t, 0.71) * 1.2;      // it swings with the washing
  for (let j = 0; j < h; j++) ctx.drawImage(TEDDY_C, 0, j, 5, 1, TEDDY.x - 2 + Math.round(sw * (j + 1) / h), TEDDY.y + j, 5, 1);
  rect(ctx, TEDDY.x - 2, TEDDY.y - 1, 1, 1, '#d8d0b8'); rect(ctx, TEDDY.x + 2, TEDDY.y - 1, 1, 1, '#d8d0b8');   // pegged by its ears
}
function drawHeroGlow(t, show) {
  const br = 0.9 + 0.1 * wave(t, 8);
  halo(HL.ifc, IFC.cx, 90, br); halo(HL.boc, BOC.cx, 118, br); halo(HL.cp, CP.cx, 110, 0.9);
  halo(HL.ctr, CTR.cx, 116, 0.8 + 0.4 * show); halo(HL.ckc, (CKC.x0 + CKC.x1) / 2, 130, 0.7 + 0.5 * show);
}
function drawHeroFx(t, show) {
  // Central Plaza's clock bars change every 15 s (its quarter hour), all four blinking just before each change
  const set = CP_SETS[step(t, 16) % CP_SETS.length], blinkOff = frac(t / 15) > 0.9 && (Math.floor(t * 4) & 1);
  if (!blinkOff) CP_BARS.forEach((y, k) => rect(ctx, CP.cx - 1, y, 3, 1, set[k]));
  halo(HL.cpTop, CP.cx, 68, 0.9); halo(HL.cpClock, CP.cx, 57, blinkOff ? 0.2 : 0.7);
  // rainbow tower: neon bands chase up the shaft
  const ph = frac(t * 20 / LOOP);
  CTR_BANDS.forEach((y, j) => { ctx.fillStyle = RAMP[Math.floor(frac(ph - j / CTR_BANDS.length * 0.6) * RAMP.length) % RAMP.length]; ctx.fillRect(CTR.cx - 5, y, 11, 1); });
  // LED facade: a new pattern every 8 s, colourful during the show
  const pat = step(t, 30), kind = (pat * 3 + 1) % 5, white = pat % 2 ? '#e0fff4' : '#6af0c0';
  const fw = GOLDFISH.find(([a, b]) => t >= a && t < b), fish = fw ? smooth(0, 1.5, t - fw[0]) * (1 - smooth(fw[1] - fw[0] - 1.5, fw[1] - fw[0], t - fw[0])) : 0;
  for (let i = 0; i < CKC_COLS; i++) for (let j = 0; j < CKC_ROWS; j++) {
    let v;
    if (kind === 0) v = 1 - Math.abs(frac(t * 60 / LOOP) - (1 - j / CKC_ROWS)) * 6;
    else if (kind === 1) { const d = frac(frac(t * (48 + 12 * (i % 3)) / LOOP + hash(i, pat, 1)) - j / CKC_ROWS); v = d < 0.3 ? 1 - d / 0.3 : 0; }
    else if (kind === 2) v = ((i + j + step(t, 480)) & 1) ? 1 : 0.12;
    else if (kind === 3) v = 0.5 + 0.5 * Math.sin(i * 0.8 + j * 0.35 - TAU * 120 * t / LOOP);
    else v = 0.55 + 0.25 * Math.sin(j * 0.3 + TAU * 30 * t / LOOP);
    v *= 1 - 0.85 * fish;                                // the facade dims for the goldfish (a hidden thing, drawn below)
    if (v < 0.08) continue;
    const [x, y] = ckcCell(i, j);
    ctx.globalAlpha = Math.min(1, v);
    ctx.fillStyle = show > 0.5 ? RAMP[Math.floor(frac(j / CKC_ROWS * 0.7 + i * 0.03 + t * 40 / LOOP) * RAMP.length) % RAMP.length] : white;
    ctx.fillRect(x, y, 1, 1);
  }
  if (fish > 0) {                                        // a goldfish in the facade's own dots, tail flicking, rising and sinking a row
    const rows = FISH_ROWS[step(t, 240) & 1], j0 = FISH_ROW + Math.round(wave(t, 30, 0.2));
    ctx.globalAlpha = fish;
    rows.forEach((row, j) => { for (let i = 0; i < CKC_COLS; i++) if (row[i] !== '.') {
      const [x, y] = ckcCell(i, j0 + j);
      ctx.fillStyle = row[i] === 'o' ? '#ffb13b' : row[i] === 't' ? '#ff7a3a' : '#fff0c8';
      ctx.fillRect(x, y, 1, 1);
    } });
  }
  ctx.globalAlpha = 1;
  halo(HL.ifcTop, IFC.cx, 42, 0.8 + 0.2 * wave(t, 12));
  if (show <= 0.01) return;
  // the show: the Bank of China lattice runs through colours, IFC's ribs carry rising pulses
  ctx.globalAlpha = show;
  const cph = frac(t * 80 / LOOP);
  for (const q of HERO.lines) { ctx.fillStyle = RAMP[Math.floor(frac(q.s * 1.5 - cph) * RAMP.length) % RAMP.length]; ctx.fillRect(q.x, q.y, 1, 1); }
  const py = 180 - frac(t * 120 / LOOP) * 140;
  HERO.ribs.forEach((x, k) => {
    const y0 = Math.round(py + (k % 2) * 12);
    ctx.fillStyle = RAMP[(k * 9 + step(t, 60) * 12) % RAMP.length];
    for (let y = Math.max(47, y0); y < Math.min(176, y0 + 9); y++) ctx.fillRect(x, y, 1, 1);
  });
  ctx.fillStyle = RAMP[Math.floor(cph * RAMP.length) % RAMP.length];
  for (const [a, top] of IFC_FINS) { ctx.fillRect(IFC.cx - a, top, 1, 2); ctx.fillRect(IFC.cx + a, top, 1, 2); }
  ctx.globalAlpha = 1;
}
function drawBeams(t, show) {
  if (show <= 0.01) return;
  const cs = step(t, 60);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter'; ctx.imageSmoothingEnabled = true;
  BEAMS.forEach((b, i) => {
    const ang = b.base + b.amp * wave(t, b.k, b.ph), co = Math.cos(ang), si = Math.sin(ang);
    ctx.globalAlpha = show * (0.7 + 0.3 * wave(t, 90 + i * 7, b.ph));
    ctx.setTransform(co, si, -si, co, b.x, b.y);
    ctx.drawImage(BEAM_C[(i + cs) % SHOW_COLS.length], -(BEAM_W - 1) / 2, -BEAM_L);
  });
  ctx.restore();
  BEAMS.forEach((b, i) => halo(HL.beamSrc[(i + cs) % SHOW_COLS.length], b.x, b.y, show * 0.9));
}
function drawFrontFx(t) {
  PIERS.forEach((x, i) => halo(HL.pier, x + 6, 186, 0.8 + 0.1 * wave(t, 400 + i * 17)));
  halo(HL.hkcec, HKCEC.x0 + 20, 182, 0.9);
  SHORE_LAMPS.forEach(([x, y], i) => halo(HL.shore, x, y, 0.75 + 0.2 * wave(t, 600 + i * 13)));
  for (const c of CARS) {
    const u = frac(t * c.n / LOOP + c.ph), x = Math.round(c.dir > 0 ? -4 + u * 488 : 484 - u * 488);
    if (x < 62 || x >= W - 1 || (x >= HKCEC.x0 && x <= HKCEC.x1) || PIERS.some(q => x >= q - 1 && x < q + 13)) continue;
    rect(ctx, x, WL - 3, 2, 1, c.dir > 0 ? '#ff5a4a' : '#fff4d0');
  }
}
/* the harbourfront rooftop sign: its block, a faint jade glow in the drizzle, the steel, the face (own ad or the
   engine's faces), the neon tube */
const BB_FLICK = [[63.2, 64.6], [149.5, 150.4], [214, 215.8]];   // the neon tube sputters
const BB_STEAM_COL = bbTone('#fff6f0'), BB_GLISTEN = bbTone('#ffffff');
const bbNeon = t => signLevel(t, BB_FLICK, 45) * (0.93 + 0.07 * wave(t, 2400, 0.4));
const BB_STEAM = [[0.1, 44, 7], [0.45, 52, 9], [0.75, 36, 11]];   // per dumpling: phase, sway cycles, swell cycles per loop
function drawBillboardAd(t) {
  ctx.drawImage(BB_AD, BB.x, BB.y);
  const u = frac(t * 30 / LOOP);                       // every 8 s the glyphs light one by one, hold, then blink twice
  BB_GLYPHS.forEach((gi, k) => {
    const on = u < 0.8 ? u > 0.06 + k * 0.08 : (u > 0.84 && u < 0.88) || u > 0.92;
    if (on) ctx.drawImage(BB_GLY_C[k], BB.x + BB_GX + k * 9, BB.y + BB_GY);
  });
  const bx = BB.x + BASKET.x, by = BB.y + BASKET.y, gs = step(t, 480);
  ctx.fillStyle = BB_STEAM_COL;
  DUMPLING_TIPS.forEach((tx, k) => {
    const [ph, ks, kw] = BB_STEAM[k], len = 4 + Math.round(1 + wave(t, kw, ph));   // steam swells and ebbs
    for (let j = 1; j <= len; j++) {
      if (frac(ks * t / LOOP + ph - j * 0.21) < 0.2) continue;    // the wisp breaks up as it rises
      const sw = Math.round(Math.sin(j * 0.9 - TAU * (ks * t / LOOP + ph)) * Math.min(1.6, j * 0.5)) + (j > 2 ? 1 : 0);
      ctx.globalAlpha = 0.85 * (1 - (j - 1) / len);
      ctx.fillRect(bx + tx + sw, by - j, 1, 1);
    }
  });
  ctx.globalAlpha = 1;
  DUMPLING_TIPS.forEach((tx, k) => { if (hash(k, gs, 29) < 0.12) rect(ctx, bx + tx - 1, by + 1, 1, 1, BB_GLISTEN); });   // a glisten
}
function drawBillboard(t, pitch) {
  const lv = bbNeon(t), { l, r: R, t: T, b: B } = BBX;
  drawLayer(BBL, t, 61);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), lv);
  blit(BBF_C);
  if (!PITCH.covers(t, pitch)) drawBillboardAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  ctx.fillStyle = BB_NEON[lv > 0.7 ? 2 : lv > 0.2 ? 1 : 0];
  ctx.fillRect(l, T, R - l + 1, 1); ctx.fillRect(l, B, R - l + 1, 1);
  ctx.fillRect(l, T + 1, 1, B - T - 1); ctx.fillRect(R, T + 1, 1, B - T - 1);
}
function drawBlinkers(t, board) {
  const I = blinkI(t);
  if (I <= 0.02) return;
  ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
  const all = [...HERO.blinks, ...(board ? BACK.blinksBB : BACK.blinks), ...PEAK_MASTS.map(x => [x, RIDGE_Y[x] - 14])];
  for (const [x, y] of all) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
}

/* ================= Per-frame: the harbour ================= */
function drawWater(t, show, board, rain) {
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: LEDGE - WL, alpha: 0.6, k1: 40, k2: 96 });
  rect(ctx, 0, WL, W, LEDGE - WL, 'rgba(6,12,24,0.18)');
  rect(ctx, 0, WL, W, 1, '#0a0e18');
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, LEDGE);
  streak(ctx, t, CTR.cx, 5, RAMP[Math.floor(frac(t * 20 / LOOP) * RAMP.length) % RAMP.length], 0.3, 11, WL + 1, LEDGE);
  streak(ctx, t, (CKC.x0 + CKC.x1) >> 1, 6, show > 0.5 ? RAMP[Math.floor(frac(t * 40 / LOOP) * RAMP.length) % RAMP.length] : '#bff8e8', 0.26, 12, WL + 1, LEDGE);
  if (board) streak(ctx, t, BB.x + (BB.w >> 1), 6, '#4cc49a', 0.1 * bbNeon(t), 14, WL + 1, LEDGE);   // the rooftop sign's neon
  if (show > 0.01) BEAMS.forEach((b, i) => { if (i < 5) streak(ctx, t, b.x + Math.round(Math.sin(b.base + b.amp * wave(t, b.k, b.ph)) * 30), 4, SHOW_COLS[(i + step(t, 60)) % 5], 0.22 * show, 20 + i, WL + 1, LEDGE); });
  const gs = step(t, 1440);                           // glints on the chop
  for (let k = 0; k < 40; k++) {
    if (hash(k, gs, 17) < 0.4) continue;
    const x = Math.floor(hash(k, gs, 18) * W), y = WL + 2 + Math.floor(hash(k, gs, 19) * (LEDGE - WL - 4));
    const warm = STREAKS.some(s => Math.abs(s.x - x) < s.w + 3);
    ctx.globalAlpha = warm ? 0.75 : 0.35; ctx.fillStyle = warm ? '#ffe8c0' : '#8a9ab8';
    ctx.fillRect(x, y, warm ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
  if (rain) drawRings(t);
}
function drawRings(t) {
  ctx.fillStyle = '#8e98b8';
  for (const g of RINGS) {
    const q = t * g.n / LOOP + g.ph, fq = Math.floor(q), cyc = fq % g.n, u = q - fq;
    if (u > 0.45) continue;
    const s = u / 0.45, x = hash(g.id, cyc, 1) * W, y = WL + 4 + hash(g.id, cyc, 2) * (LEDGE - WL - 6);
    const rr = 1 + s * 3.5, ry = rr * 0.32, pts = 6 + Math.floor(rr * 2.5);
    ctx.globalAlpha = (1 - s) * 0.45;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
/** rippled mirror of a boat sprite under its waterline */
function boatReflect(spr, x, wl, rows, a, t) {
  const w = spr.width, h = spr.height;
  rows = Math.min(rows, LEDGE - wl - 1, h);
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = a * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
}
function drawWake(x, w, wl, dir, len, t) {
  const stern = dir > 0 ? x + 2 : x + w - 3, bow = dir > 0 ? x + w - 1 : x, s = step(t, 1200);
  ctx.fillStyle = '#e8f0f4';
  for (let d = 1; d < len; d++) {
    const xx = stern - dir * d;
    if (xx < 0 || xx >= W) continue;
    const fade = 1 - d / len, yn = wl + 1 + Math.round(d * 0.1);
    if (yn < LEDGE && hash(d, s, 41) < 0.7) { ctx.globalAlpha = 0.65 * fade; ctx.fillRect(xx, yn, 1, 1); }
    if (hash(d, s, 42) < 0.55) { ctx.globalAlpha = 0.4 * fade; ctx.fillRect(xx, wl - Math.round(d * 0.04), 1, 1); }
    if (d < len * 0.6 && hash(d, s, 43) < 0.9 * fade) { ctx.globalAlpha = 0.85 * fade; ctx.fillRect(xx, wl + (hash(d, s, 44) < 0.5 ? 0 : 1), 1, 1); }
  }
  ctx.globalAlpha = 0.85;
  for (let k = 0; k < 6; k++) if (hash(k, s, 45) < 0.8) ctx.fillRect(bow + dir * (k >> 1), wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
}
function drawFerry(f, t) {
  if (t < f.a || t >= f.b) return;
  const spr = FERRY_C, w = spr.width, h = spr.height, dir = f.x1 > f.x0 ? 1 : -1;
  const x = Math.round(lerp(f.x0, f.x1, (t - f.a) / (f.b - f.a))), wl = f.wl + Math.round(wave(t, 50, f.a / 100) * 0.6);
  boatReflect(spr, x, wl, 10, 0.34, t);
  drawWake(x, w, wl, dir, 80, t);
  ctx.drawImage(spr, x, wl - h + 1);
  for (const o of [-14, 0, 14]) streak(ctx, t, x + (w >> 1) + o, 3, '#ffd98a', 0.3, o + 7, wl + 1, LEDGE);
  halo(HL.ferry, x + (w >> 1), wl - 6, 0.7);
  if (frac(t / 2) < 0.5) rect(ctx, dir > 0 ? x + 5 : x + w - 6, wl - h + 2, 1, 1, '#ff4a4a');   // on the stern mast
}
function drawJunk(t) {
  if (t < JUNK.a || t >= JUNK.b) return;
  const w = JUNK_C.width, h = JUNK_C.height, x = Math.round(lerp(JUNK.x0, JUNK.x1, (t - JUNK.a) / (JUNK.b - JUNK.a)));
  const wl = JUNK.wl + Math.round(wave(t, 40, 0.3) * 0.6);
  boatReflect(JUNK_C, x, wl, 14, 0.36, t);
  drawWake(x, w, wl, -1, 40, t);
  ctx.drawImage(JUNK_C, x, wl - h + 2);
  halo(HL.junk, x + 22, wl - 14, (0.75 + 0.1 * wave(t, 300)) * clamp((400 - x - 22) / 24, 0, 1));   // fades in from behind the hut
  streak(ctx, t, x + 20, 8, '#ff7a4a', 0.3, 33, wl + 1, LEDGE);
}
function drawLaunch(b, t) {
  const u = frac(b.k * t / LOOP + b.ph), x = Math.round(b.dir > 0 ? lerp(-14, W + 4, u) : lerp(W + 4, -14, u));
  const spr = LAUNCH_C[b.dir > 0 ? 0 : 1], wl = b.wl + Math.round(wave(t, 70 + b.k * 9, b.ph) * 0.7);
  boatReflect(spr, x, wl, 5, 0.3, t);
  drawWake(x, spr.width, wl, b.dir, 22, t);
  ctx.drawImage(spr, x, wl - spr.height + 1);
  halo(HL.launch, x + (b.dir > 0 ? 4 : 6), wl - 5, 0.9);
  streak(ctx, t, x + 5, 2, '#ffc868', 0.3, b.k * 5, wl + 1, LEDGE);
}

/* ================= Per-frame: the tenement, its sign and laundry ================= */
function drawNWindow(w, t) {
  const { x, y, w: ww, h: hh } = w;
  if (w.kind === 'tv') {
    const s = step(t, 1200), I = 0.45 + 0.55 * hash(13 + w.id, s);
    rect(ctx, x, y, ww, hh, '#0a0e22');
    ctx.globalAlpha = I * 0.7; rect(ctx, x, y, ww, hh, TVC[Math.floor(hash(12 + w.id, s) * TVC.length)]); ctx.globalAlpha = 1;
    halo(HL.tv, x + ww / 2, y + hh / 2, I);
    return;
  }
  let on = true, cool = w.kind === 'cool';
  if (w.kind === 'toggle') { on = hash(w.id + 50, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < w.p; cool = w.id % 3 === 0; }
  if (!on) return;
  if (cool) { rect(ctx, x, y, ww, hh, '#c8f0e2'); rect(ctx, x, y, ww, 4, '#e8fff6'); rect(ctx, x, y + hh - 3, ww, 3, '#9ad0c0'); }
  else { rect(ctx, x, y, ww, hh, '#f6b462'); rect(ctx, x, y, ww, 5, '#ffdc9c'); rect(ctx, x + ww - 5, y, 5, hh, '#c87a3c'); }
  if (w.id === 6) {                                    // someone crossing the room now and then
    const u = frac(t / 40), px0 = x + Math.round(lerp(-3, ww + 1, smooth(0.2, 0.55, u)));
    if (px0 >= x - 2 && px0 < x + ww) { ctx.fillStyle = cool ? '#4a6a64' : '#6a4228'; ctx.fillRect(Math.max(x, px0), y + 4, Math.min(3, x + ww - px0), 9); ctx.fillRect(Math.max(x, px0 + 1), y + 2, 1, 2); }
  }
  halo(cool ? HL.winCool : HL.win, x + ww / 2, y + hh / 2, 0.8);
}
function sway(t, ph) { return wave(t, 20, ph) * 0.9 + wave(t, 47, ph + 0.3) * 0.5 + wave(t, 5, ph) * 0.4; }
function drawCloth(ci, x, y, t, ph) {
  const spr = CLOTH_C[ci], w = spr.width, h = spr.height, sw = sway(t, ph) * 1.6;
  for (let j = 0; j < h; j++) ctx.drawImage(spr, 0, j, w, 1, x - (w >> 1) + Math.round(sw * (j + 1) / h), y + j, w, 1);
  rect(ctx, x - (w >> 1) + 1, y - 1, 1, 1, '#d8d0b8'); rect(ctx, x + (w >> 1) - 1, y - 1, 1, 1, '#d8d0b8');   // pegs
}
function signLevel(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 24), 3);
  return hv > 0.55 ? 1 : hv > 0.3 ? 0.4 : 0;
}
function drawSprite(on, off, x, y, lv) {
  if (lv >= 1) { ctx.drawImage(on, x, y); return; }
  ctx.drawImage(off, x, y);
  if (lv > 0) { ctx.globalAlpha = lv; ctx.drawImage(on, x, y); ctx.globalAlpha = 1; }
}
const DRIPS = NACS.map((a, k) => ({ x: a.x + 1 + (k % 2) * 7, y0: a.y + 8, y1: a.y + 30, n: 50 + k * 9, ph: k * 0.23 }));
DRIPS.push({ x: HAC.x + 2, y0: HAC.y + 9, y1: FLOOR + 3, n: 57, ph: 0.61 }, { x: HAC.x + 11, y0: HAC.y + 9, y1: FLOOR + 3, n: 73, ph: 0.12 });
function drawDrips(t) {
  const g = 420;
  ctx.fillStyle = '#d4ecff';
  for (const d of DRIPS) {
    const T = LOOP / d.n, q = t / T + d.ph, u = q - Math.floor(q), tau = u * T, rel = T * 0.55;
    if (tau < rel) { ctx.globalAlpha = 0.2 + 0.6 * tau / rel; ctx.fillRect(d.x, d.y0, 1, 1); continue; }
    const s = tau - rel, y = d.y0 + 0.5 * g * s * s;
    if (y < d.y1 - 1) { ctx.globalAlpha = 0.85; ctx.fillRect(d.x, Math.round(y), 1, 2); continue; }
    const ss = (s - Math.sqrt(2 * (d.y1 - 1 - d.y0) / g)) / 0.3;
    if (ss >= 1) continue;
    const hy = d.y1 - 1 - Math.round(Math.sin(Math.PI * ss) * 2), sp = 1 + Math.round(ss * 2);
    ctx.globalAlpha = 1 - ss;
    ctx.fillRect(d.x - sp, hy, 1, 1); ctx.fillRect(d.x + sp, hy, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawNeighbour(t) {
  blit(NB_C);
  for (const w of NWIN) drawNWindow(w, t);
  ctx.fillStyle = '#3a4446';                           // window cages
  for (const w of NWIN) { for (let x = w.x + 2; x < w.x + w.w; x += 3) ctx.fillRect(x, w.y - 1, 1, w.h + 1); ctx.fillRect(w.x - 1, w.y + w.h, w.w + 2, 1); }
  const hum = 0.93 + 0.07 * wave(t, 2400);
  const rl = signLevel(t, ROOF_FLICK, 41);
  drawSprite(RSIGN_C[1], RSIGN_C[0], 6, 26, rl); halo(HL.roofSign, 26, 32, rl * hum);
  const sl = signLevel(t, SIGN_FLICK, 40);
  drawSprite(SIGN_C[1], SIGN_C[0], SIGN.x, SIGN.y, sl);
  halo(HL.sign, SIGN.x + 7, SIGN.y + 30, sl * hum); halo(HL.sign, SIGN.x + 7, SIGN.y + 72, sl * hum);
  halo(HL.signPink, SIGN.x + 7, SIGN.y + SIGN.h - 6, sl * 0.8);
  drawCockatoo(t);
  for (const pl of POLES) pl.clothes.forEach(([x, ci], k) => drawCloth(ci, x, pl.y + 2, t, k * 0.17 + pl.y * 0.01));
  drawTeddy(t);
  drawDrips(t);
}

/* ================= Neighbours on our roof =================
   An uncle on the red stool fans himself with a palm-leaf fan and follows the races on his radio; a couple shares a
   clear umbrella at the parapet; an auntie comes out of the stair hut to take the washing in with her forked bamboo
   pole and later hangs it out again; a young man comes out to film the light show. Seen from behind they face the
   harbour, so the skyline catches their hair and shoulders; in the lit doorway they are silhouettes.
   The roof's timeline (loop seconds):
     all loop         the uncle fans himself, resting the fan now and then, and sips his tea every 40 s; she leans
                      on his shoulder under the umbrella, he points something out across the water
     LAUNDRY[0] 20    the auntie takes the washing in, piece by piece, into her basin (about 43 s)
     RACE 58-66       the race on the uncle's radio: he leans in to it, then lifts his fan for his horse
     GUEST 99-173     a young man comes out for the light show (SHOW 104-172) and films it by the hut; she films it too
     LAUNDRY[1] 182   the auntie hangs the washing out again (about 43 s)                                          */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
function line(x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  ctx.fillStyle = col;
  for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(x0 + (x1 - x0) * k / n), Math.round(y0 + (y1 - y0) * k / n), 1, 1);
}
const NIGHT = '#0b0d18', RIM = '#c4d4f0', DOORGLOW = '#ffb860';
const SKINS = ['#ecc49c', '#d8a87e', '#b8845c'], HAIRS = ['#16120f', '#2c241e', '#6e6e78', '#3a2a22'];
/** a look: proportions for a height of h px, colours dimmed for the night (dk) and edged by the light behind them (rim);
    o.sleeve: rows of sleeve (0 bare arms), o.shorts: rows of shorts above bare legs, o.skirt, o.long hair, o.perm, o.bald */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.4, rim = o.rim || RIM, m = c => mixHex(c, NIGHT, dk), d = c => mixHex(c, NIGHT, Math.min(0.9, dk + 0.22));
  const P = { h, hh: 6, hw: o.hw || 5, th: Math.round(h * 0.37), tw: o.tw || (h >= 27 ? 8 : 7),
    skin: m(SKINS[skin]), skinD: d(SKINS[skin]), skinL: mixHex(m(SKINS[skin]), rim, 0.5), hair: m(HAIRS[hair]), hairL: mixHex(m(HAIRS[hair]), rim, 0.45),
    top: m(top), topD: d(top), topL: mixHex(m(top), rim, 0.42), bot: m(bot), botD: d(bot), shoe: m(o.shoe || '#4a3434'),
    sleeve: o.sleeve ?? 3, shorts: o.shorts || 0, skirt: o.skirt || 0, long: o.long || 0, perm: !!o.perm, bald: !!o.bald,
    singlet: !!o.singlet, print: o.print ? m(o.print) : null };
  P.ll = h - P.hh - P.th;
  if (!o.sil) P.sil = look(h, skin, hair, top, bot, { ...o, dk: 0.8, rim: DOORGLOW, sil: true });   // backlit in the doorway
  return P;
}
/** one arm from its shoulder (sx, sy) to the hand (hx, hy): sleeve, then bare arm, then the hand */
function arm(P, sx, sy, hx, hy) {
  const n = Math.max(Math.abs(hx - sx), Math.abs(hy - sy), 1), up = hy < sy - 1;   // a raised arm catches the light
  for (let k = 0; k < n; k++) R(Math.round(sx + (hx - sx) * k / n), Math.round(sy + (hy - sy) * k / n), 1, 1, k < P.sleeve ? (up ? P.top : P.topD) : up ? P.skin : P.skinD);
  R(hx, hy, 1, 1, P.skin);
}
/**
 * One neighbour, feet on row fy, centred on column x. Returns { tx, ty, bw, hy, hx } for the props they hold.
 * o.view: 0 from behind (facing the harbour), 1 facing us, 2 in profile (o.dir: 1 right, -1 left); o.step: stride frame;
 * o.sit: seated on a stool with the hips on row fy; o.lean / o.dip: head px sideways and down; o.hL / o.hR: [x, y] of a
 * raised hand (left and right as we see them)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bw = side ? tw - 3 : tw, tx = x - (bw >> 1), ty = fy - legs - th + 1, hy = ty - hh;
  if (legs) {
    const ly = fy - legs + 1, legC = P.shorts || P.skirt ? P.skinD : P.bot;
    if (side) {
      if (o.step) {
        for (const [d, lift] of [[-dir, 1], [dir, 0]]) {
          for (let j = 0; j < legs - 2 - lift; j++) R(x - 1 + Math.round(d * j * 3 / (legs - 2)), ly + 2 + j, 2, 1, j < P.shorts ? P.bot : legC);
          R(x - 1 + d * 3 + (dir > 0 ? 0 : -1), fy - lift, 3, 1, P.shoe);
        }
      } else { R(x - 1, ly + 2, 3, legs - 2, legC); if (P.shorts) R(x - 1, ly + 2, 3, P.shorts, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 4, 1, P.shoe); }
      R(x - 2, ly, 4, 2, P.bot);
      if (P.skirt) R(x - 2, ly, 5, P.skirt, P.bot);
    } else {
      const lw = 3, lift = o.step ? 1 : 0, sw = o.stepSide || 0;
      R(tx, ly, bw, 2, P.bot);
      R(tx, ly + 2, lw, legs - 2 - (sw < 0 ? lift : 0), legC); R(tx + bw - lw, ly + 2, lw, legs - 2 - (sw > 0 ? lift : 0), legC);
      if (P.shorts) { R(tx, ly + 2, lw, P.shorts, P.bot); R(tx + bw - lw, ly + 2, lw, P.shorts, P.bot); }
      if (P.skirt) { R(tx - 1, ly, bw + 2, P.skirt, P.bot); R(tx - 1, ly + P.skirt - 1, bw + 2, 1, P.botD); }
      R(tx, fy - (sw < 0 ? lift : 0), lw, 1, P.shoe); R(tx + bw - lw, fy - (sw > 0 ? lift : 0), lw, 1, P.shoe);
    }
  } else if (o.sit) { R(tx - 1, fy - 1, bw + 2, 2, P.bot); R(tx - 1, fy, bw + 2, 1, P.botD); }
  // the torso: shirt, its shaded side, the shoulders lit from beyond
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);
  if (P.singlet && !side) { R(tx, ty, 2, 2, P.skin); R(tx + bw - 2, ty, 2, 2, P.skin); R(tx + (bw >> 1) - 1, ty, 2, 1, P.skin); R(tx, ty, 1, 1, P.skinL); R(tx + bw - 1, ty, 1, 1, P.skinL); }
  if (P.print) for (let j = 2; j < th - 1; j += 2) for (let i = (j >> 1) & 1; i < bw - 1; i += 3) R(tx + i, ty + j, 1, 1, P.print);   // a flowered blouse
  // arms
  if (side) {
    const ax = x + (o.step ? -dir : 0);
    if (o.hR) arm(P, x, ty + 1, o.hR[0], o.hR[1]);
    else arm(P, x, ty + 1, ax, ty + th - 1);
  } else {
    const sy = ty + 1;
    if (o.hL) arm(P, tx - 1, sy, o.hL[0], o.hL[1]); else arm(P, tx - 1, sy, tx - 1, ty + th - 1);
    if (o.hR) arm(P, tx + bw, sy, o.hR[0], o.hR[1]); else arm(P, tx + bw, sy, tx + bw, ty + th - 1);
  }
  // the head
  const hx = x - (hw >> 1) + (o.lean || 0), hY = hy + (o.dip || 0);
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair);
    if (P.bald) { R(hx, hY, hw, 1, P.skin); R(hx + 1, hY + 1, hw - 2, 2, P.skin); R(hx + 1, hY, hw - 2, 1, P.skinL); }
    else R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair);
    else { R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin); if (!P.perm) { R(hx - 1, hY + 2, 1, 2, P.skinD); R(hx + hw, hY + 2, 1, 2, P.skinD); } }
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, 2, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, 2, P.hair); R(hx, hY, hw, 1, P.hairL);
    R(dir > 0 ? hx : hx + hw - 2, hY, 2, hh - 1 + P.long, P.hair);
    R(dir > 0 ? hx + hw : hx - 1, hY + 3, 1, 1, P.skinD);                       // the nose
  }
  if (P.perm) { R(hx - 1, hY, hw + 2, 3, P.hair); R(hx, hY - 1, hw, 1, P.hair); R(hx, hY - 1, hw, 1, P.hairL); R(hx - 1, hY, 1, 1, P.hairL); R(hx + hw, hY, 1, 1, P.hairL); }
  return { tx, ty, bw, hy: hY, hx };
}

/* ----- who they are ----- */
const UNCLE = look(28, 1, 2, '#f6f4ee', '#3a4a5e', { dk: 0.3, tw: 9, sleeve: 0, singlet: true, bald: true, shoe: '#2a4a8a' });
const HIM = look(28, 0, 0, '#2c4468', '#3a3a44', { dk: 0.36 });
const HER = look(25, 0, 0, '#e8a0a8', '#e8e4dc', { dk: 0.36, long: 4, skirt: 6, tw: 7, shoe: '#e8e0d8' });
const AUNTIE = look(25, 1, 1, '#d87a98', '#24242c', { dk: 0.34, perm: true, tw: 8, shoe: '#c83a3a', print: '#f4e0a0' });
const GUEST_P = look(27, 2, 0, '#eeeee6', '#2a2e3a', { shorts: 5, shoe: '#2a2a30' });
const COUPLE = { him: 240, her: 249, fy: 245 };
const UMB_X = 245, UMB_TOP = 207;                     // the clear umbrella's tip
const UNCLE_X = STOOL.x + 7, SEAT = 235;              // on the stool, hips on its seat
const RACE = 58;                                      // the race on his radio: he leans in, then cheers with his fan at RACE + 6
const DOOR_X = DOOR.x + 8, OUT_Y = 247, WALK = 14;    // the doorway's centre, the roof row they walk on, px a second
const walkF = t => Math.floor(t * 3.2) & 1;           // stride frame (3.2 a second: a whole number per loop)

/** a scripted visit out of the stair hut: moves { a, b, x0, x1, y0, y1, view, dir, kind, k, fade } (loop seconds) */
function script(a0, stops) {
  const S = [];
  let t = a0, x = DOOR_X, y = FLOOR;
  const add = (dur, m) => { const x1 = m.x ?? x, y1 = m.y ?? y; S.push({ ...m, a: t, b: t + dur, x0: x, x1, y0: y, y1 }); t += dur; x = x1; y = y1; };
  add(1.2, { kind: 'fade', view: 1, fade: 1 });                       // out of the stairwell's light
  add(1.2, { kind: 'walk', view: 1, y: OUT_Y });                      // a step down onto the roof
  for (const s of stops) {
    if (s.x !== x) add(Math.max(0.6, Math.abs(s.x - x) / WALK), { kind: 'walk', view: 2, dir: s.x < x ? -1 : 1, x: s.x });
    add(s.dur, { ...s, view: 0 });
  }
  add(Math.max(0.6, Math.abs(DOOR_X - x) / WALK), { kind: 'walk', view: 2, dir: 1, x: DOOR_X });
  add(1.2, { kind: 'walk', view: 0, y: FLOOR });                      // back up into the doorway
  add(1.2, { kind: 'fade', view: 0, fade: -1 });
  return S;
}
// she works from the hut's end of the line to the post taking the washing in, and back again hanging it out
const WORK = 5.5;
const LAUNDRY = [
  script(20, [4, 3, 2, 1, 0].map(k => ({ kind: 'take', k, x: LINE_CLOTHES[k][0] - 5, dur: WORK }))),
  script(182, [0, 1, 2, 3, 4].map(k => ({ kind: 'hang', k, x: LINE_CLOTHES[k][0] - 5, dur: WORK }))),
];
const TAKE_AT = [], HANG_AT = [];                     // when her pole work on each piece starts
LAUNDRY[0].forEach(m => { if (m.kind === 'take') TAKE_AT[m.k] = m.a; });
LAUNDRY[1].forEach(m => { if (m.kind === 'hang') HANG_AT[m.k] = m.a; });
const GUEST = script(99, [{ kind: 'watch', x: 393, dur: 63.4 }]);
const pegged = (k, t) => t < TAKE_AT[k] + 1.5 || t >= HANG_AT[k] + 2.9;   // a piece of washing hangs on the line
const inBasin = t => LINE_CLOTHES.reduce((n, _, k) => n + (t >= TAKE_AT[k] + 4 && t < HANG_AT[k] + 0.3 ? 1 : 0), 0);
const moveAt = (S, t) => S.find(m => t >= m.a && t < m.b);
/** where a scripted neighbour is: x, feet row, view, stride, how faded in, and the move */
function placeOf(m, t) {
  const u = (t - m.a) / (m.b - m.a), walk = m.kind === 'walk';
  return { x: Math.round(lerp(m.x0, m.x1, u)), fy: Math.round(lerp(m.y0, m.y1, u)), view: m.view, dir: m.dir,
    step: walk && walkF(t), alpha: m.fade > 0 ? u : m.fade < 0 ? 1 - u : 1 };
}
/** the forked bamboo pole: tip (fork) at (x, y), leaning back to the butt 27 rows below */
function pole(x, y) {
  line(x, y + 1, x - 1, y + 27, '#a08a52');
  for (let j = 5; j < 27; j += 6) R(x - (j > 13 ? 1 : 0), y + 1 + j, 1, 1, '#6a5a34');            // its nodes
  R(x - 1, y - 1, 1, 1, '#c8b070'); R(x + 1, y - 1, 1, 1, '#c8b070'); R(x, y, 1, 1, '#c8b070');   // the fork
}
/** the auntie's red basin on her hip, with what she has taken in */
function basin(bx, by, n, dark) {
  R(bx, by, 7, 1, dark ? '#5a2a1c' : '#ff6a5a'); R(bx, by + 1, 7, 2, dark ? '#3a1612' : '#c8282a'); R(bx + 1, by + 3, 5, 1, dark ? '#2a0e0c' : '#8a161c');
  for (let k = 0; k < n; k++) R(bx + 1 + k, by - 1 - (k & 1), 1 + (k & 1), 1 + (k & 1), CLOTHES[LINE_CLOTHES[k][1]].pal[1]);
}
function drawAuntie(t) {
  const S = t < 120 ? LAUNDRY[0] : LAUNDRY[1], m = moveAt(S, t);
  if (!m) return;
  const p = placeOf(m, t), work = m.kind === 'take' || m.kind === 'hang', P = p.fy < OUT_Y - 2 ? AUNTIE.sil : AUNTIE;
  const s = t - m.a, k = m.k, ix = work ? LINE_CLOTHES[k][0] : 0, ly = work ? lineY(ix) : 0, rest = OUT_Y - 35;
  let tipY = rest, cloth = null;                       // cloth: [x, y] of the piece in her pole's fork or on its way
  if (m.kind === 'take') {
    tipY = s < 1.5 ? lerp(rest, ly, smooth(0.3, 1.5, s)) : s < 1.8 ? ly - 2 * smooth(1.5, 1.8, s) : lerp(ly - 2, rest, smooth(1.8, 3.4, s));
    if (s >= 1.5 && s < 3.4) cloth = [0, 0];
  } else if (m.kind === 'hang') {
    tipY = s < 1 ? rest : s < 2.6 ? lerp(rest, ly - 2, smooth(1, 2.6, s)) : s < 2.9 ? ly - 2 + 2 * smooth(2.6, 2.9, s) : lerp(ly, rest, smooth(2.9, 4.2, s));
    if (s >= 1 && s < 2.9) cloth = [0, 0];
  }
  ctx.globalAlpha = p.alpha;
  const hi = clamp((rest - tipY) / (rest - ly + 2), 0, 1), tipX = work ? Math.round(lerp(ix - 1, ix, hi)) : p.x + 4;
  tipY = Math.round(tipY);
  const back = p.view === 0, handY = Math.round(Math.max(tipY + 14, p.fy - 26));
  const b = person(p.x, p.fy, P, { view: p.view, dir: p.dir, step: p.step, stepSide: p.step ? 1 : -1,
    hR: back ? [tipX - (tipX > p.x + 4 ? 1 : 0), handY] : null });
  // the basin on her left hip, her arm round it
  const bx = p.view === 2 ? (p.dir > 0 ? b.tx + b.bw - 2 : b.tx - 5) : b.tx - 4, by = b.ty + P.th - 3;
  basin(bx, by, inBasin(t), P === AUNTIE.sil);
  if (p.view !== 2) R(b.tx - 1, by - 1, 1, 2, P.skin);
  // the pole: upright in her hand while she walks, up to the line while she works
  if (back) pole(tipX, tipY);
  else if (p.view === 2) pole(p.x + p.dir * 2, p.fy - 34);
  else pole(b.tx + b.bw + 1, p.fy - 34);
  if (cloth) drawCloth(LINE_CLOTHES[k][1], tipX, tipY + 1, t, 0.4 + k * 0.21);
  else if (work) {                                     // into the basin, or out of it
    const v = m.kind === 'take' ? smooth(3.4, 4, s) : 1 - smooth(0.3, 1, s), on = m.kind === 'take' ? s >= 3.4 && s < 4 : s >= 0.3 && s < 1;
    if (on) drawCloth(LINE_CLOTHES[k][1], Math.round(lerp(tipX, bx + 3, v)), Math.round(lerp(tipY + 1, by - 6, v)), t, 0.4 + k * 0.21);
  }
  ctx.globalAlpha = 1;
}
function drawGuest(t) {
  const m = moveAt(GUEST, t);
  if (!m) return;
  const p = placeOf(m, t), P = p.fy < OUT_Y - 2 ? GUEST_P.sil : GUEST_P, s = t - m.a;
  // he films the show with his phone up, rests his arm a while in the middle, then films its finale
  const film = m.kind === 'watch' && ((s > 6 && s < 40) || (s > 48 && s < 62));
  ctx.globalAlpha = p.alpha;
  const o = { view: p.view, dir: p.dir, step: p.step, stepSide: p.step ? 1 : -1 };
  if (film) { const top = p.fy - P.h + 1; o.hR = [p.x + 3, top + 4]; o.hL = [p.x - 3, top + 5]; }
  const b = person(p.x, p.fy, P, o);
  if (film) phone(p.x - 1, b.hy + 1, t);
  ctx.globalAlpha = 1;
}
/** a phone held up in front of the face, seen from behind: its screen shows the show */
function phone(x, y, t) {
  R(x - 1, y - 1, 5, 4, '#10121a');
  R(x, y, 3, 2, RAMP[Math.floor(frac(t * 80 / LOOP) * RAMP.length) % RAMP.length]);
  R(x, y, 3, 1, '#e8f4ff');
}
/** the couple in one pose (baked): her head on his shoulder (lean), her phone up (film), his arm out (point) */
function couplePose(lean, film, point) {
  const fy = COUPLE.fy, herTop = fy - HER.h + 1, himTop = fy - HIM.h + 1;
  // her: arm through his, her head on his shoulder now and then, her phone up for the show
  person(COUPLE.her, fy, HER, { lean: lean ? -2 : 0, dip: lean ? 1 : 0, hL: [COUPLE.her - 5, herTop + 12], hR: film ? [COUPLE.her + 2, herTop + 5] : null });
  // him: the umbrella in his right hand, his left pointing across the water now and then
  person(COUPLE.him, fy, HIM, { hR: [UMB_X, himTop + 10], hL: point ? [COUPLE.him - 8, himTop + 1] : null });
}
/** the umbrella's shaft, the crook of its handle and the canopy, leaning by sway px (baked) */
function umbrellaPose(sway) {
  const himTop = COUPLE.fy - HIM.h + 1;
  line(UMB_X, himTop + 9, UMB_X + sway, UMB_TOP + 1, '#3a3e48');
  R(UMB_X - 1, himTop + 11, 1, 2, '#3a3e48');
  ctx.drawImage(UMB_C, UMB_X + sway - (UMB_C.width >> 1), UMB_TOP);
}
function drawCouple(t, show) {
  const lean = frac(t / 40 + 0.2) < 0.35, point = frac(t / 40 + 0.72) < 0.07;
  const film = show > 0.5 && ((t > 108 && t < 128) || (t > 152 && t < 162)), sway = Math.round(wave(t, 11, 0.3) * 0.7);
  ctx.drawImage(COUPLE_C[(lean && !film ? 1 : 0) + (film ? 2 : 0) + (point ? 4 : 0)], COUPLE_BOX[0], COUPLE_BOX[1]);
  if (film) phone(COUPLE.her - 2, COUPLE.fy - HER.h + 2, t);
  ctx.drawImage(UMB_POSE_C[sway + 1], COUPLE_BOX[0], COUPLE_BOX[1]);
  const s = step(t, 480);                                                                // drizzle beading on the canopy
  ctx.fillStyle = '#f4f8ff';
  for (let q = 0; q < 5; q++) if (hash(q, s, 61) < 0.6) {
    const dx = Math.round((hash(q, s, 62) - 0.5) * 20), r = Math.abs(dx);
    ctx.fillRect(UMB_X + sway + dx, UMB_TOP + 1 + (r > 9 ? 5 : r > 6 ? 3 : r > 3 ? 2 : 1), 1, 1);
  }
}
/** the clear umbrella: a pale dome you can see the city through, its ribs and rim catching the light (prerendered) */
function makeUmbrella() {
  const HW = [1, 4, 7, 9, 10, 11, 11], w = 25, c = 12, p = pix(w, 9);
  HW.forEach((hw, j) => { for (let dx = -hw; dx <= hw; dx++) p.set(c + dx, j + 1, '#dce8f8', Math.abs(dx) >= hw - 1 ? 150 : 52); });
  for (let dx = -11; dx <= 11; dx++) if ((dx + 11) % 5 !== 4) p.set(c + dx, 8, '#e8f0fc', 190);   // the scalloped rim
  for (const e of [-11, -5, 5, 11]) for (let j = 1; j <= 7; j++) p.set(c + Math.round(e * j / 7), j + 1, '#eef4ff', 120);
  p.set(c, 0, '#2a2e38'); p.set(c, 1, '#c8d4e8');
  return p.done();
}
/** the uncle on the stool: palm-leaf fan in his left hand, tea from the cap on his right, his radio on the cap */
const FAN = [['.###.', '#####', '#####', '#####', '.###.', '..#..'], ['.##.', '.##.', '###.', '.##.', '.##.', '..#.'], ['.#', '.#', '.#', '.#', '.#', '.#']];
const UNCLE_FAN = 5, UNCLE_BOX = [138, 200, 36, 47];   // fan states: three frames of a stroke, resting, held up
/** the uncle in one pose (baked): fan state f (0-2 a stroke, 3 resting on his knee, 4 held up), sipping, leaning in */
function unclePose(f, sip, race) {
  const P = UNCLE, x = UNCLE_X, ty = SEAT - P.th + 1, hy = ty - P.hh, tx = x - (P.tw >> 1);
  // his calves under the seat and his slippers on the stool's rung
  R(x - 4, 239, 2, 6, P.skinD); R(x + 2, 239, 2, 6, P.skinD); R(x - 5, 245, 3, 1, P.shoe); R(x + 2, 245, 3, 1, P.shoe);
  const o = { sit: true, lean: race ? -1 : 0, dip: race ? 1 : 0 };
  let fan;                                             // [x, y, frame] of the fan's top-left
  if (f === 4) { o.hL = [tx - 2, hy - 3]; fan = [tx - 4, hy - 10, 0]; }
  else if (f < 3) { o.hL = [tx - 3 + (f === 2 ? 1 : 0), hy + 6]; fan = [tx - 5 + (f === 0 ? -1 : f === 2 ? 2 : 0), hy, f]; }
  else fan = [tx - 3, SEAT - 3, 2];
  if (sip) o.hR = [tx + P.tw, hy + 5];
  person(x, SEAT, P, o);
  const g = FAN[fan[2]];
  g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') R(fan[0] + i, fan[1] + j, 1, 1, j === g.length - 1 ? '#5a4a2a' : i === 0 || j === 0 ? '#c8b07a' : (i + j) & 1 ? '#a08a56' : '#8a7648'); });
}
const sipping = t => frac(t / 40 + 0.15) < 0.07;
function drawUncle(t) {
  const sip = sipping(t), race = t >= RACE && t < RACE + 6, cheer = t >= RACE + 6 && t < RACE + 7.6;
  const ph = frac(t * 150 / LOOP), f = cheer ? 4 : race || frac(t / 20 + 0.3) >= 0.72 ? 3 : ph < 0.25 ? 0 : ph < 0.5 || ph >= 0.75 ? 1 : 2;
  ctx.drawImage(UNCLE_C[(f * 2 + (sip ? 1 : 0)) * 2 + (race ? 1 : 0)], UNCLE_BOX[0], UNCLE_BOX[1]);
  // the mug sits on the cap unless it is at his lips
  if (!sip) {
    for (let y = MUG.y; y < MUG.y + 4; y++) R(MUG.x, y, 3, 1, y === MUG.y ? '#8a5a2a' : '#c8c0b8');
    R(MUG.x, MUG.y + 1, 1, 3, '#f4f0ea'); R(MUG.x + 3, MUG.y + 1, 1, 1, '#c8c0b8'); R(MUG.x + 3, MUG.y + 2, 1, 1, '#a8a098');
  }
  // the radio's dial, a little brighter while the race is called
  R(RADIO.x + 6, RADIO.y + 2, 3, 1, race ? '#ffd070' : '#e8a040');
  halo(HL.dial, RADIO.x + 7, RADIO.y + 2, race ? 0.9 : 0.55);
  return sip;
}
/** a sprite of what draw() paints inside the box [x0, y0, w, h] of the scene (build time) */
function bake([x0, y0, w, h], draw) {
  const [c, x] = makeCanvas(w, h), keep = ctx;
  x.translate(-x0, -y0); ctx = x; draw(); ctx = keep;
  return c;
}
const COUPLE_BOX = [COUPLE.him - 14, UMB_TOP - 1, 34, COUPLE.fy - UMB_TOP + 2];
let COUPLE_C, UMB_POSE_C, UNCLE_C;
function bakePeople() {
  COUPLE_C = [0, 1, 2, 3, 4, 5, 6, 7].map(k => bake(COUPLE_BOX, () => couplePose(!!(k & 1), !!(k & 2), !!(k & 4))));
  UMB_POSE_C = [-1, 0, 1].map(sw => bake(COUPLE_BOX, () => umbrellaPose(sw)));
  UNCLE_C = [];
  for (let f = 0; f < UNCLE_FAN; f++) for (const sip of [0, 1]) for (const race of [0, 1]) UNCLE_C.push(bake(UNCLE_BOX, () => unclePose(f, sip, race)));
}
function drawPeople(t, show) {
  drawCouple(t, show);
  drawAuntie(t);
  drawGuest(t);
  return drawUncle(t);
}

/* ================= Per-frame: our rooftop ================= */
function tubeLevel(t) {
  let v = 0.95 + 0.05 * wave(t, 1500, 0.3);
  if (inEvent(t, TUBE_FLICK)) { const hv = hash(11, Math.floor(t * 20)); v *= hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.1; }
  return v;
}
function drawCat(t) {
  const X = CAT.x, Y = CAT.y, turn = HEAD_TURNS.find(([a, b]) => t >= a && t < b), hd = turn ? (t < (turn[0] + turn[1]) / 2 ? -1 : 1) : 0;
  ctx.drawImage(CAT_C, 0, 5, 11, 8, X, Y + 5, 11, 8);
  ctx.drawImage(CAT_C, 0, 0, 11, 5, X + hd, Y, 11, 5);
  if (EAR_FLICKS.some(e => t >= e && t < e + 0.35)) { rect(ctx, X + 7 + hd, Y, 1, 1, '#1a2030'); rect(ctx, X + 8 + hd, Y + 1, 1, 1, '#8a9ab8'); }
  // tail hanging over the front of the parapet, swishing
  const sw = 0.8 * wave(t, 23, 0.3) + 0.4 * wave(t, 61, 0.1);
  ctx.fillStyle = '#08080e';
  for (let j = 0; j < 10; j++) ctx.fillRect(X + 9 + Math.round(sw * j * j / 30), LEDGE - 1 + j, 1 + (j < 2 ? 1 : 0), 1);
}
function drawPigeons(t) {
  for (const pg of PIGEONS) {
    const s = step(t, 480), h = hash(pg.k, Math.floor(s / 3), 7);
    const flap = frac(pg.k * t / LOOP + pg.ph) < 0.03;
    const pose = flap ? 'flap' : h < 0.35 && (s & 1) ? 'peck' : 'up';
    const dir = hash(pg.k, Math.floor(s / 40), 9) < 0.5 ? pg.dir : -pg.dir;
    ctx.drawImage(PIG_C[pose][dir > 0 ? 0 : 1], pg.x + (pose === 'up' && h > 0.8 ? 1 : 0), pg.y);
  }
}
function drawRoof(t) {
  halo(HL.street, 110, LEDGE + 16, 0.9 + 0.1 * wave(t, 700)); halo(HL.streetJade, 330, LEDGE + 18, 0.9);
  blit(ROOF_C);
  // the hut: warm stairwell light, cool tube lamp over the door, a lit window
  const tl = tubeLevel(t);
  for (let y = DOOR.y; y < FLOOR; y++) { ctx.fillStyle = y < DOOR.y + 6 ? '#ffd490' : y < 222 ? '#f0b060' : '#c88040'; ctx.fillRect(DOOR.x + 1, y, DOOR.w - 2, 1); }
  rect(ctx, DOOR.x + 3, DOOR.y + 4, 4, 38, '#a86a30'); rect(ctx, DOOR.x + 11, 228, 4, 14, '#6a4020');
  halo(HL.door, DOOR.x + 8, 222, 0.85);
  rect(ctx, TUBE.x + 1, TUBE.y, TUBE.w - 2, 1, tl > 0.6 ? '#f4fff8' : '#6a7a74');
  halo(HL.tube, TUBE.x + 9, TUBE.y + 2, tl); halo(HL.tubeCore, TUBE.x + 9, TUBE.y + 1, tl);
  for (let k = 0; k < 2; k++) { const a = TAU * (30 + k * 7) * t / LOOP + k * 2; rect(ctx, Math.round(TUBE.x + 9 + Math.cos(a) * (6 + k * 3)), Math.round(TUBE.y + 4 + Math.sin(2 * a) * 3), 1, 1, '#f4f0e0'); }
  const wl = hash(3, Math.floor(frac(t / LOOP + 0.3) * 5), 4) < 0.7;
  if (wl) { rect(ctx, HWIN.x, HWIN.y, HWIN.w, HWIN.h, '#f0a858'); rect(ctx, HWIN.x, HWIN.y, HWIN.w, 4, '#ffd08a'); ctx.fillStyle = '#6a4a2a'; for (let x = HWIN.x; x < HWIN.x + HWIN.w; x += 3) ctx.fillRect(x, HWIN.y, 1, HWIN.h); halo(HL.hwin, HWIN.x + 10, HWIN.y + 6, 0.8); }
  // wet floor: light spills from the door and the tube
  streak(ctx, t, DOOR.x + 8, 12, '#ffb860', 0.28, 71, FLOOR + 1, H);
  streak(ctx, t, TUBE.x + 9, 8, '#d0fff0', 0.18 * tl, 72, FLOOR + 1, H);
  streak(ctx, t, SIGN.x + 7, 6, '#34e0a1', 0.16, 73, FLOOR + 1, H);
  const ps = step(t, 480);                             // puddles shimmer with neon and skyline colours
  PUDDLES.forEach(([cx, cy, rx], k) => {
    const cols = k === 0 ? ['#34e0a1', '#ff4f9a'] : ['#ffd28a', '#cfe0ff'];
    for (let q = 0; q < 6; q++) {
      if (hash(k, q, ps) < 0.4) continue;
      ctx.globalAlpha = 0.45; ctx.fillStyle = cols[q & 1];
      ctx.fillRect(Math.round(cx - rx * 0.7 + hash(k * 9 + q, ps, 3) * rx * 1.4), cy - 1 + (q % 3), 2, 1);
    }
  });
  ctx.globalAlpha = 1;
  // laundry on our line
  ctx.fillStyle = '#9a9a8a';
  for (let x = LPOST.x + 1; x < HUT.x; x++) ctx.fillRect(x, lineY(x), 1, 1);
  LINE_CLOTHES.forEach(([x, ci], k) => { if (pegged(k, t)) drawCloth(ci, x, lineY(x) + 1, t, 0.4 + k * 0.21); });
  drawCat(t);
  drawPigeons(t);
  const sip = drawPeople(t, showLevel(t));
  if (sip) return;
  // tea steam from the uncle's mug on the cap
  for (let k = 0; k < 5; k++) {
    const u = frac(t * (44 + k * 5) / LOOP + k * 0.2), x = MUG.x + 2 + Math.round(wave(t, 30 + k * 4, k * 0.1) * 1.5 * u + u * 3);
    ctx.globalAlpha = 0.5 * Math.min(1, u * 6) * (1 - u);
    ctx.drawImage(PUFF_C[1 + (u > 0.5 ? 1 : 0)], x - 1, Math.round(MUG.y - 2 - u * 14) - 1);
  }
  ctx.globalAlpha = 1;
  halo(HL.mug, MUG.x + 2, MUG.y + 3, 0.6);
}

/* ================= Rain (drizzle), tinted by nearby lights ================= */
function drawRain(L, t, board) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -12 + hash(d.id, cyc, 11) * (W + 24 - L.fall * L.slope * 0.5);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      const li = litAt(xh, yh, board);
      ctx.fillStyle = li < 0 ? L.col : L.lit[li];
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh + k * L.slope), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 6;
      ctx.globalAlpha = (1 - s) * (L.water ? 0.6 : 1);
      ctx.fillStyle = L.splash;
      if (L.water) {
        const rr = 1 + s * 2.5;
        ctx.fillRect(Math.round(xl - rr), Math.round(yl), 1, 1); ctx.fillRect(Math.round(xl + rr), Math.round(yl), 1, 1);
      } else {
        const hgt = Math.sin(Math.PI * s) * (big ? 3 : 1.5), spread = 1 + s * (big ? 3 : 1.5), Y = Math.round(yl - 1 - hgt);
        ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
        if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      }
      ctx.globalAlpha = 1;
    }
  }
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's
// own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over'; ctx.imageSmoothingEnabled = false;
  const show = showLevel(t), wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawHeli(t);
  drawPeak(t);
  blit(MID_C);
  if (wx.drizzle) drawRain(RAIN[0], t, billboard);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawLayer(BACK, t, 11);
  drawBackSigns(t);
  drawFog(FOGS[0], t);
  drawHeroGlow(t, show);
  if (billboard) drawBillboard(t, pitch);
  drawLayer(HERO, t, 23);
  drawHeroFx(t, show);
  drawBeams(t, show);
  drawLayer(FRONT, t, 37);
  drawFrontFx(t);
  drawBlinkers(t, billboard);
  drawWater(t, show, billboard, wx.drizzle);
  drawLaunch(LAUNCHES[2], t);
  drawJunk(t);
  drawLaunch(LAUNCHES[0], t);
  drawFog(FOGS[1], t);
  drawFerry(FERRIES[0], t);
  drawFerry(FERRIES[1], t);
  drawLaunch(LAUNCHES[1], t);
  if (wx.drizzle) drawRain(RAIN[1], t, billboard);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawNeighbour(t);
  drawRoof(t);
  if (wx.drizzle) drawRain(RAIN[2], t, billboard);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'hong-kong',
  name: 'Hong Kong',
  tagline: 'Victoria Harbour after dark',
  timeZone: 'Asia/Hong_Kong',
  accent: '#34e0a1',
  focusX: FOCUS,
  landmark: [BOC.cx - 11, IFC.cx + 10],                // Bank of China Tower to the crowned tower
  landmarkTop: 37,                                     // the row of the crowned tower's spikes, which landscape crops keep
  billboard: BB,
  perch: { dy: -1 },                                   // the pigeon stands on the neon tube, the row above the face
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: BOC.cx - 11, y: 40, w: 23, h: 136 },                          // Bank of China Tower
    { kind: 'landmark', x: IFC.cx - 10, y: 37, w: 21, h: 139 },                          // the crowned tower
    { kind: 'water', x: 108, y: WL + 1, w: 230, h: 16 },                                 // Victoria Harbour, left of our laundry line
    { kind: 'birds', x: PIGEONS[0].x - 3, y: PIGEONS[0].y - 3, w: 13, h: 10 },           // the pigeon on the stair hut's roof
  ],
  secrets: [                                           // hidden things a tap finds, each a passport sticker (site/js/ui/secrets.js)
    { id: 'cockatoo', name: 'a cockatoo on the neon sign', x: COCKATOO.x - 1, y: COCKATOO.y - 1, w: 8, h: 8, art: [
      '...y.y......', '..yyyy......', '...wwww.....', '..wwwkwK....', '..wwwwwKK...', '..wwwwwK....',
      '...wwwwW....', '...wwwwwW...', '...wwwwwW...', '....wwwW....', '....S.S.....', 'pppppppppppp'] },
    { id: 'teddy', name: 'a teddy bear on the washing line', x: TEDDY.x - 3, y: TEDDY.y - 2, w: 7, h: 11, art: [
      'yyyyyyyyyyyy', '..w......w..', '..nn....nn..', '..nnnnnnnn..', '..nknnnnkn..', '..nnnyynnn..',
      '...nnkknn...', '.nnnnnnnnnn.', '.nN.nyyn.Nn.', '....nyyn....', '...nnNNnn...', '...nn..nn...'] },
    { id: 'goldfish', name: 'a goldfish on the LED tower', x: CKC.x0 + 1, y: CKC.top + 4 + (FISH_ROW - 1) * 3 - 1, w: CKC.x1 - CKC.x0 - 1, h: 7 * 3,
      at: GOLDFISH, art: [
        '............', '............', '...ooo....o.', '..ooooo..oo.', '.owkoooo.oO.', '.oooooooooO.',
        '.oooooooooO.', '..oooOO..oO.', '...OOO....O.', '............', '..b..b......', '............'] },
  ],
  ambience: { water: 0.4, rain: 0.3, traffic: 0.25, crowd: 0.15 },
  events: [
    { t: HORNS[0], type: 'horn', x: 70 },
    { t: HORNS[0] + 0.5, type: 'boat', dur: 84, pan: [-0.8, 0.7], gain: 0.4 },
    { t: SHOW[0], type: 'chime' },
    { t: HORNS[1], type: 'horn', x: 400 },
    { t: HORNS[1] + 0.4, type: 'boat', dur: 82, pan: [0.7, -0.8], gain: 0.4 },
    { t: 140, type: 'chime' },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
